import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/controls/customControls/dialog";
import { Button } from "../../../components/controls/customControls/button";
import {
  Select,
  SelectContent,
  SelectContentScrollable,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "../../../components/controls/customControls/select";
import { Input } from "../../../components/controls/customControls/input";
import { Label } from "../../../components/controls/customControls/label";
import { v4 as uuidv4 } from "uuid";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useScheduleData, UseScheduleDataReturn } from "../scheduleData";
import {
  getPatientByPatientId,
  getPatientAppointmentsByPatientId,
  getPatientsListPowerSearch,
  setFilteredPatients,
  addNewPatient,
  addNewPatientMatch,
} from "../../../dispatchers/patientDispatchers";
import { getCompanyVaccineByCompanyId } from "../../../dispatchers/nmsDispatchers";
import useTitanApp from "../../../hooks/useTitanApp";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../components/controls/customControls//radio-group";
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from "../../../components/controls/customControls/alert";
import {
  AlertCircle,
  ArrowLeft,
  Bell,
  Calendar,
  Search,
  AlertTriangle,
  Syringe,
  Plus,
  CheckSquare,
  Trash2,
  Globe,
  Store,
  TriangleAlert,
  CircleAlert,
  Info,
} from "lucide-react";
import { trackFirstTimeIntercomEvent } from "@/utils/trackFirstTimeIntercomEvent";
import { getFormattedDate } from "@/utils/FormattedDate";

import {
  setShowBookingSlots,
  setIsConsultationRoute,
} from "@/dispatchers/scheduleEventsDispatchers";
import { PatientMatchDialog } from "@/pages/Dashboard/ServicesTab/ServicesContainer/ServicesKanban/PatientMatchDialog";
import { useNHS111Data } from "@/lib/nhs111/nhs111Data";

import {
  setConsultationLoadError,
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import { accessTitanverseConsultationUpsert } from "@/data/constantStrings";
import { VaccineCombobox } from "../schedule/VaccineComboBox";
import {
  AccordionItem,
  AccordionTrigger,
  Accordion,
  AccordionContent,
} from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as DatePickerCalendar } from "@/components/ui/calendar";
import {
  clearDefaultCalenderAppointmentPharmacyServices,
  clearServiceUsersByCompany,
  getDefaultServicesByCompanyIdAndPharmacyId,
} from "@/dispatchers/pharmacyServiceDispatchers";
import { Badge } from "../../../components/ui/badge";
import { Switch } from "../../../components/ui/switch";

const NOTES_MAX_LENGTH = 500;
const TASK_MAX_LENGTH = 1000;
const charLimitMessage = (max: number) =>
  `Character limit exceeded. Maximum ${max} characters allowed.`;

/**
 * Calculate search relevance score for a patient
 * Higher score = better match
 */
const calculateRelevanceScore = (patient: any, searchQuery: string): number => {
  const query = searchQuery.toLowerCase().trim();
  let score = 0;

  // Extract searchable fields
  const name = (patient.patientName || "").toLowerCase();
  const nhs = (patient.nhsNumber || "").toLowerCase();
  const address = (patient.address || "").toLowerCase();

  // 1. Exact match (highest priority) - 1000 points
  if (name === query) score += 1000;
  if (nhs === query) score += 1000;

  // 2. Starts with query - 500 points
  if (name.startsWith(query)) score += 500;
  if (nhs.startsWith(query)) score += 500;
  if (address.startsWith(query)) score += 300;

  // 3. Word boundary match (word starts with query) - 400 points
  const nameWords = name.split(/\s+/);
  const addressWords = address.split(/\s+/);

  nameWords.forEach((word) => {
    if (word.startsWith(query)) score += 400;
  });

  addressWords.forEach((word) => {
    if (word.startsWith(query)) score += 200;
  });

  // 4. Contains query - 200 points
  if (name.includes(query)) score += 200;
  if (nhs.includes(query)) score += 200;
  if (address.includes(query)) score += 100;

  // 5. Position bonus (earlier in string = higher score)
  const nameIndex = name.indexOf(query);
  if (nameIndex !== -1) {
    score += Math.max(0, 100 - nameIndex * 2); // Earlier positions get higher bonus
  }

  const nhsIndex = nhs.indexOf(query);
  if (nhsIndex !== -1) {
    score += Math.max(0, 100 - nhsIndex * 2);
  }

  // 6. Length similarity bonus (closer to query length = better)
  if (name.length > 0) {
    const lengthDiff = Math.abs(name.length - query.length);
    score += Math.max(0, 50 - lengthDiff * 2);
  }

  // 7. Consecutive character match bonus
  for (let i = 0; i < name.length - query.length + 1; i++) {
    if (name.substring(i, i + query.length) === query) {
      score += 300; // Bonus for consecutive match
    }
  }

  return score;
};

/**
 * Sort patients by relevance to search query
 */
const sortPatientsByRelevance = (
  patients: any[],
  searchQuery: string,
): any[] => {
  if (!searchQuery || searchQuery.trim().length === 0) {
    return patients;
  }

  return [...patients].sort((a, b) => {
    const scoreA = calculateRelevanceScore(a, searchQuery);
    const scoreB = calculateRelevanceScore(b, searchQuery);

    // Higher score first
    return scoreB - scoreA;
  });
};

type BookingForm = {
  patientName: string;
  patientId?: string;
  pharmacist: string;
  serviceType: string;
  date: string;
  time: string;
  duration: string;
  room?: string;
  notes?: string;
  email?: string;
  mobile?: string;
  confirmationMethod?: "email" | "mobile" | undefined;
  // these fields are not part of the react-hook-form inputs; the modal
  // adds them from local state before submitting so the parent can persist
  tasksToComplete?: AppointmentAction[];
  vaccinesAdminister?: AppointmentVaccine[];
};

export interface AppointmentVaccine {
  id: string;
  name: string;
  vaccineName?: string;
  dose?: string;
  notes?: string;
  completed?: boolean;
  prescriptionItemId?: string;
  manufacturer?: string;
}
interface Booking {
  id: string;
  patientName: string;
  pharmacist: string;
  serviceType: string;
  date: string;
  time: string;
  duration: number;
  room?: string; // display name
  roomId?: string; // id for prefilling
  notes?: string;
  patientId?: string;
  email?: string;
  contactNumber?: string;
  confirmationMethod?: "email" | "mobile";
  serviceId?: string;
  appointmentType?: number;
}

interface BookingModalProps {
  trigger?: React.ReactNode;
  mode?: "create" | "edit";
  booking?: Booking;
  isCalendarEdit?: boolean;
  onSave: (
    booking: BookingForm & {
      tasksToComplete?: AppointmentAction[];
      vaccinesAdminister?: AppointmentVaccine[];
    },
  ) => void;
  onCancel?: () => void;
  editMode?: Booking; // Keep for backward compatibility
  pharmacists: string[];
  serviceTypes?: string[];
  rooms?: string[];
  initialDate?: string;
  initialTime?: string;
  initialDuration?: string | number;
  initialServiceTypeId?: string;
  initialPharmacistName?: string;
  initialPatientName?: string;
  initialPatientId?: string;
  initialPatientEmail?: string;
  initialPatientPhone?: string;
  isDarkMode?: boolean;
  isPartiallyMatched?: boolean; // Made optional
  onStartConsultation?: (booking: Booking) => void;
  onDelete?: (bookingId: string) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onPatientMatchClick?: (matchingPatients: any[]) => void; // Made optional
  isReadOnly: boolean;
  tasksToComplete?: AppointmentAction[];
  vaccinesAdminister?: AppointmentVaccine[];
  newTaskLabel?: string;
  newVaccineId?: string;
  setNewTaskLabel?: (label: string) => void;
  setNewVaccineId?: (id: string) => void;
  onTaskAdd?: (label: string) => void;
  onTaskRemove?: (id: string) => void;
  onVaccineAdd?: (vaccine: AppointmentVaccine | string) => void; // string kept for backward compat
  onVaccineRemove?: (id: string) => void;
}

interface AppointmentAction {
  id: string;
  label: string;
  completed?: boolean;
}

export function BookingModal({
  trigger,
  mode,
  booking,
  isCalendarEdit = false,
  onSave,
  onCancel,
  editMode,
  rooms = [],
  initialDate,
  initialTime,
  initialDuration,
  initialServiceTypeId,
  initialPharmacistName,
  initialPatientName,
  initialPatientId,
  initialPatientEmail,
  initialPatientPhone,
  isDarkMode = false,
  onStartConsultation,
  onDelete,
  isOpen: externalIsOpen,
  onOpenChange,
  onPatientMatchClick,
  isPartiallyMatched = false, // Default value
  isReadOnly = false,
  tasksToComplete,
  vaccinesAdminister,
  newTaskLabel = "",
  newVaccineId = "",
  setNewTaskLabel = () => {},
  setNewVaccineId = () => {},
  onTaskAdd = () => {},
  onTaskRemove = () => {},
  onVaccineAdd = () => {},
  onVaccineRemove = () => {},
}: BookingModalProps) {
  // internal state for task/vaccine when parent doesn’t control them
  const [internalTaskLabel, setInternalTaskLabel] =
    React.useState(newTaskLabel);
  const [internalVaccineId, setInternalVaccineId] =
    React.useState(newVaccineId);

  // local arrays so we can add/remove multiple entries
  const [localTasks, setLocalTasks] = React.useState<AppointmentAction[]>(
    tasksToComplete ?? [],
  );
  const [localVaccines, setLocalVaccines] = React.useState<
    AppointmentVaccine[]
  >(vaccinesAdminister ?? []);

  // Define isEditMode early so effects can use it
  const isEditMode = mode === "edit" || !!editMode;

  // Define currentBooking early - needed by effects
  const currentBooking = booking || editMode;

  // Define open early - needed by effects
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [isPharmacistLoading, setIsPharmacistLoading] = React.useState(false);
  const [BookingConfirmationLoading, setBookingConfirmationLoading] =
    React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isRoomsLoading, setIsRoomsLoading] = React.useState(false);
  // Custom calendar popover for the Date field - only fires on an explicit
  // day click, never on the picker's month navigation (unlike the native
  // <input type="date">, which fires onChange while merely navigating).
  const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false);
  const open =
    externalIsOpen !== undefined ? externalIsOpen : mode ? true : internalOpen;
  const setOpen = React.useCallback(
    (value: boolean) => {
      if (onOpenChange) {
        onOpenChange(value);
      } else if (onCancel && !value) {
        onCancel();
      } else {
        setInternalOpen(value);
      }
    },
    [onOpenChange, onCancel],
  );

  React.useEffect(() => {
    if (!open) {
      const cleanup = () => {
        document.body.style.pointerEvents = "";
        if (document.body.style.overflow === "hidden") {
          document.body.style.overflow = "";
        }
      };
      cleanup();
      const timer = setTimeout(cleanup, 50);
      const timer2 = setTimeout(cleanup, 200);
      return () => {
        clearTimeout(timer);
        clearTimeout(timer2);
      };
    }
  }, [open]);

  React.useEffect(() => {
    return () => {
      document.body.style.pointerEvents = "";
      if (document.body.style.overflow === "hidden") {
        document.body.style.overflow = "";
      }
    };
  }, []);

  React.useEffect(() => {
    setInternalTaskLabel(newTaskLabel);
  }, [newTaskLabel]);
  React.useEffect(() => {
    setInternalVaccineId(newVaccineId);
  }, [newVaccineId]);

  React.useEffect(() => {
    if (open && isEditMode && currentBooking) {
      const rawTasks =
        (currentBooking as any)?.tasksToComplete ||
        (currentBooking as any)?.TasksToComplete ||
        tasksToComplete ||
        [];

      const parsedTasks: AppointmentAction[] = Array.isArray(rawTasks)
        ? rawTasks.map((t: any, idx: number) => {
            if (typeof t === "string") {
              return {
                id: String(idx + 1),
                label: t,
                text: t,
                completed: false,
              };
            }
            return {
              id: String(t.id || t.Id || t._id || idx + 1),
              label: t.label || t.text || t.name || t.Name || String(t),
              text: t.text || t.label || t.name || t.Name || String(t),
              completed: Boolean(t.completed),
            };
          })
        : [];

      const rawVaccines =
        (currentBooking as any)?.vaccinesAdminister ||
        (currentBooking as any)?.vaccinesToAdminister ||
        (currentBooking as any)?.VaccinesAdminister ||
        (currentBooking as any)?.VaccinesToAdminister ||
        vaccinesAdminister ||
        [];

      const parsedVaccines: AppointmentVaccine[] = Array.isArray(rawVaccines)
        ? rawVaccines.map((v: any, idx: number) => {
            if (typeof v === "string") {
              return { id: String(idx + 1), name: v, vaccineName: v, dose: "" };
            }
            const vName =
              v.VaccineName || v.vaccineName || v.Name || v.name || "Vaccine";
            return {
              id: String(v.Id || v.id || v._id || idx + 1),
              name: vName,
              vaccineName: vName,
              dose: v.Manufacturer || v.dose || "",
              manufacturer: v.Manufacturer || v.manufacturerName || "",
              prescriptionItemId:
                v.PrescriptionItemId || v.prescriptionItemId || "",
              productId: v.productId || "",
              productName: v.productName || "",
            };
          })
        : [];

      setLocalTasks(parsedTasks);
      setLocalVaccines(parsedVaccines);
    }
  }, [tasksToComplete, vaccinesAdminister, isEditMode, open, currentBooking]);

  // For new appointments, clear state when modal opens and keep cleared
  React.useEffect(() => {
    if (open && !isEditMode && !currentBooking) {
      setLocalTasks([]);
      setLocalVaccines([]);
      setInternalTaskLabel("");
      setInternalVaccineId("");
    }
  }, [open, isEditMode, currentBooking]);

  const slotSelectedDate = React.useMemo(
    () => (initialDate && initialDate.trim() ? initialDate.trim() : ""),
    [initialDate],
  );

  const taskLabel = internalTaskLabel;
  const vaccineIdState = internalVaccineId;

  const handleTaskLabelChange = (val: string) => {
    setInternalTaskLabel(val);
    setNewTaskLabel(val);
  };

  const validateTemplateAvailability = (
    data: BookingForm,
    confirmCancelTemplates: any,
    setValue: any,
    setError: any,
    operationType: string,
  ): boolean => {
    clearErrors("confirmationMethod");

    if (data.confirmationMethod) {
      if (data.confirmationMethod === "email") {
        if (operationType === "insert") {
          if (
            !confirmCancelTemplates?.confirmationCancellation?.emailConfirmation?.trim()
          ) {
            setValue("confirmationMethod", "" as any);
            setError("confirmationMethod", {
              type: "manual",
              message:
                "No Email confirmation template is configured for this service.",
            });
            return false;
          }
        } else if (operationType === "delete") {
          if (
            !confirmCancelTemplates?.confirmationCancellation?.emailCancellation?.trim()
          ) {
            setValue("confirmationMethod", "" as any);
            setError("confirmationMethod", {
              type: "manual",
              message:
                "No Email cancellation template is configured for this service.",
            });
            return false;
          }
        } else {
          if (!confirmCancelTemplates?.reschedule?.emailReschedule?.trim()) {
            setValue("confirmationMethod", "" as any);
            setError("confirmationMethod", {
              type: "manual",
              message:
                "No Email reschedule template is configured for this service.",
            });
            return false;
          }
        }
      } else if (data.confirmationMethod === "mobile") {
        if (operationType === "insert") {
          if (
            !confirmCancelTemplates?.confirmationCancellation?.smsConfirmation?.trim()
          ) {
            setValue("confirmationMethod", "" as any);
            setError("confirmationMethod", {
              type: "manual",
              message:
                "No SMS confirmation template is configured for this service.",
            });
            return false;
          }
        } else if (operationType === "delete") {
          if (
            !confirmCancelTemplates?.confirmationCancellation?.smsCancellation?.trim()
          ) {
            setValue("confirmationMethod", "" as any);
            setError("confirmationMethod", {
              type: "manual",
              message:
                "No SMS cancellation template is configured for this service.",
            });
            return false;
          }
        } else {
          if (!confirmCancelTemplates?.reschedule?.smsReschedule?.trim()) {
            setValue("confirmationMethod", "" as any);
            setError("confirmationMethod", {
              type: "manual",
              message:
                "No SMS reschedule template is configured for this service.",
            });
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleDelete = () => {
    const formData = getValues();
    if (
      !validateTemplateAvailability(
        formData,
        confirmCancelTemplates,
        setValue,
        setError,
        "delete",
      )
    ) {
      return;
    }
    setShowDeleteConfirm(true);
  };

  const handleVaccineIdChange = (val: string) => {
    setInternalVaccineId(val);
    setNewVaccineId(val);
  };

  const handleAddTask = (label: string) => {
    if (!label.trim()) {
      console.warn("attempted to add empty task");
      return;
    }
    const newTask: AppointmentAction = { id: uuidv4(), label };
    setLocalTasks((prev) => {
      const updated = [...prev, newTask];
      return updated;
    });
    onTaskAdd(label);
    handleTaskLabelChange("");
  };

  const handleRemoveTask = (id: string) => {
    setLocalTasks((prev) => {
      const updated = prev.filter((t) => t.id !== id);
      return updated;
    });
    onTaskRemove(id);
  };
  const handleRemoveVaccine = (id: string) => {
    setLocalVaccines((prev) => prev.filter((v) => v.id !== id));
    onVaccineRemove && onVaccineRemove(id);
  };
  const handleAddVaccine = (id: string) => {
    if (!id.trim()) {
      console.warn("attempted to add vaccine with empty id");
      return;
    }
    const selected = flattenedProducts.find((v: any) => v.uniqueId === id);
    const name = selected ? selected.vaccineName : id;

    const prescriptionItemId =
      selected?.selectedProduct?.prescribedItemId || "";
    const manufacturer =
      selected?.selectedProduct?.manufacturerName ||
      selected?.manufacturer ||
      "";
    const productId = selected?.selectedProduct?.productId || "";
    const productName = selected?.selectedProduct?.productName || "";

    const newV: AppointmentVaccine = {
      id: uuidv4(),
      name,
      vaccineName: name,
      prescriptionItemId,
      manufacturer,
      productId,
      productName,
    };
    setLocalVaccines((prev) => {
      const updated = [...prev, newV];
      return updated;
    });
    onVaccineAdd && onVaccineAdd(newV);
    handleVaccineIdChange("");
  };

  // Use mode/booking if provided, otherwise fall back to editMode for backward compatibility
  const s = useScheduleData();
  const { appState, patientState, scheduleEventState, pharmacyServiceState } =
    useTitanApp();

  const {
    defaultCalenderAppointmentPharmacyServices,
    ClinicalServiceBuilder,
    isPharmacyServiceLoading,
    isServiceUsersByCompanyLoading,
    isUpdatePharmacyLocationLoading,
  } = pharmacyServiceState || {};
  const { currentPharmacyId, companyId } = appState || {};
  const activePharmacyId =
    (isEditMode &&
      ((currentBooking as any)?.PharmacyID ||
        (currentBooking as any)?.pharmacyID ||
        (currentBooking as any)?.pharmacyId ||
        (currentBooking as any)?.otherPharmacyId ||
        (currentBooking as any)?.OtherPharmacyId)) ||
    currentPharmacyId;
  const { filteredPatients, selectedPatientId, consultationId } =
    patientState || {};
  const {
    showBookingSlots,
    bookingSlotsMode,
    showBacktoSlotsButton,
    isConsultationRoute,
  } = scheduleEventState || {};

  const { nmsServiceState } = useTitanApp();
  const { companyVaccines } = nmsServiceState || {};

  const confirmCancelTemplates = ClinicalServiceBuilder[0]?.product;

  const flattenedProducts = React.useMemo(() => {
    const results = [];
    const seenIds = new Set();
    (companyVaccines || []).forEach((item: any) => {
      const vaccine =
        item?.value && typeof item.value === "object" ? item.value : item;
      if (vaccine && vaccine.vaccineName) {
        (vaccine.products || []).forEach((product: any) => {
          const uniqueId = `${vaccine._id}-${product.productId}`;
          if (!seenIds.has(uniqueId)) {
            results.push({
              ...vaccine,
              selectedProduct: product,
              uniqueId,
            });
            seenIds.add(uniqueId);
          }
        });
      }
    });
    return results;
  }, [companyVaccines]);
  const [showMatchDialog, setShowMatchDialog] = React.useState(false);

  // Local state for partial matches
  const [localPartialMatch, setLocalPartialMatch] = React.useState(false);
  const [localMatchingPatients, setLocalMatchingPatients] = React.useState<
    any[]
  >([]);

  const {
    servicePharmacists = [],
    serviceTypesInfo = [],
    isEventCalendarLoading = false,
    isPatientDetailsLoading,
    onServiceTypeSelected,
    resetServiceUsersFetchGuard,
    onShowEditBooking,
    onPharmacistSelected,
    onAnyPharmacistSelected,
    serviceRooms = [],
    pharmacyRooms = [],
    serviceDurations = [],
    availableDates = [],
    activePharmacistsRequest = null,
    pharmacyAvailableSlotsDate = "",
    pharmacyAvailableSlotsCount = 0,
    isPharmacistBookedAtSlot,
    getAvailableTimesForDate,
    getAggregatedTimesForDate,
    getServicePharmacistSlotsForDate,
    getServiceRoomSlotsForDate,
    getPharmacistWeekAvailabilitySlots,
    formatDateFromDate,
    validatePharmacySlots,
    validateBookingConflicts,
    handleGetAppointmentRooms,
    isAppointmentRoomsLoading = false,
    currentUser,
    selectedPatientConsultation,
    defaultPharmacyServices = [],
    bookingModalServices = [],
  } = (s || {}) as Partial<UseScheduleDataReturn>;
  const [selectedPatient, setSelectedPatient] = React.useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

  const [showAllServiceTimes, setShowAllServiceTimes] = React.useState(false);
  // Times where every pharmacist is already booked, keyed by service|date|duration|time.
  const [fullyBookedTimes, setFullyBookedTimes] = React.useState<string[]>([]);
  const [bookedSlotMessage, setBookedSlotMessage] = React.useState<
    string | null
  >(null);
  // Stops the time auto-fill effect from re-picking a time right after we cleared it.
  const suppressTimeAutoFillRef = React.useRef(false);
  // Date the user just picked whose pharmacy-slots check hasn't finished yet.
  const pickedDateInFlightRef = React.useRef<string | null>(null);

  const clearBookedSlotNotice = React.useCallback(() => {
    setBookedSlotMessage(null);
    suppressTimeAutoFillRef.current = false;
  }, []);

  React.useEffect(() => {
    setShowAllServiceTimes(false);
    setFullyBookedTimes([]);
    clearBookedSlotNotice();
    resetServiceUsersFetchGuard?.();
  }, [open, resetServiceUsersFetchGuard, clearBookedSlotNotice]);

  const handlePatientMatch = (localPatientId: string) => {
    // Find the matched patient from localMatchingPatients
    const matchedPatient = [
      ...(localMatchingPatients.length > 0
        ? localMatchingPatients
        : filteredPatients || []),
    ].find((p) => (p.patientID || p._id) === localPatientId);

    if (matchedPatient) {
      // Create the patient name for display
      const patientDisplayName =
        matchedPatient.patientName ||
        `${matchedPatient.foreName || ""} ${
          matchedPatient.surName || ""
        }`.trim();

      setPatientSearchQuery(patientDisplayName);

      setValue("patientName", patientDisplayName, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue("patientId", localPatientId, {
        shouldValidate: true,
        shouldDirty: true,
      });

      // Set email if available
      if (matchedPatient.email) {
        setValue("email", matchedPatient.email, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      // Set mobile if available
      if (matchedPatient.contacts?.length > 0) {
        const mobileContact = matchedPatient.contacts.find(
          (c) => c.contactType === 4,
        );
        if (mobileContact?.value) {
          setValue("mobile", mobileContact.value, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }

      setShowPatientResults(false);
    }

    // Close the dialog and clear partial match state
    setShowMatchDialog(false);
    setLocalPartialMatch(false);
    setLocalMatchingPatients([]);
  };
  const initialValues = {
    PersonalDetails: {
      ForeName: "",
      MiddleName: "",
      SurName: "",
      DOB: null,
    },
    ContactInformation: {
      Telephone: "",
      Email: "",
    },
  };
  const {
    NHSEmergencyServiceData,
    NHSEmergencyPatientData,
    formatDate,
    matchDialogOpen,
    setMatchDialogModal,
    handleCardClick,
    matchPatient,
    isNHSEmergencyPatientLoading,
    handleAddNewPatient,
  } = useNHS111Data();
  // Patient search state
  const patientInputRef = React.useRef<HTMLInputElement | null>(null);
  const [patientSearchQuery, setPatientSearchQuery] = React.useState("");
  const [showQuickAddPatient, setShowQuickAddPatient] = React.useState(false);
  const [showPatientResults, setShowPatientResults] = React.useState(false);
  const [patientSearchLoading, setPatientSearchLoading] = React.useState(false);
  const [quickAddDefaults, setQuickAddDefaults] = React.useState({
    forename: "",
    surname: "",
    dateOfBirth: "",
    email: "",
    telephone: "",
  });

  type QuickAddForm = {
    forename: string;
    surname: string;
    dateOfBirth: string;
    email?: string;
    telephone?: string;
  };

  function InlineQuickAddPatient({
    isDarkMode,
    defaultValues,
    onCancel,
    onSave,
  }: {
    isDarkMode: boolean;
    defaultValues: QuickAddForm;
    onCancel: () => void;
    onSave: (patient: QuickAddForm) => void; // Change to pass the form data
  }) {
    const [form, setForm] = React.useState<QuickAddForm>(defaultValues);
    const [isSaving, setIsSaving] = React.useState(false);
    const [fieldErrors, setFieldErrors] = React.useState<
      Partial<Record<keyof QuickAddForm, string>>
    >({});
    const handleChange = (field: keyof QuickAddForm, value: string) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      if (fieldErrors[field]) {
        setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

    const handleSubmit = async () => {
      const errors: Partial<Record<keyof QuickAddForm, string>> = {};
      if (!form.forename.trim()) errors.forename = "Forename is required";
      if (!form.surname.trim()) errors.surname = "Surname is required";
      if (!form.dateOfBirth) errors.dateOfBirth = "Date of birth is required";

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
      setIsSaving(true);

      try {
        // Pass the form data to the parent
        await onSave(form);
      } catch (err) {
        console.error("Error saving patient:", err);
        setIsSaving(false);
      }
    };

    return (
      <div
        className={`rounded-lg border p-4 space-y-3 ${
          isDarkMode
            ? "bg-gray-700/50 border-gray-600"
            : "bg-blue-50/50 border-blue-200"
        }`}
      >
        {/* Rest of your component remains the same */}
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">Quick Add Patient</span>
          <button
            type="button"
            onClick={onCancel}
            className="text-xs text-blue-600"
          >
            Back to search
          </button>
        </div>

        {/* <div className="grid grid-cols-2 gap-3">
          <input
            placeholder="Forename *"
            type="text"
            value={form.forename}
            onChange={(e) => handleChange("forename", e.target.value)}
            className="px-2 py-1 text-sm border rounded"
          />
          <input
            placeholder="Surname *"
            value={form.surname}
            onChange={(e) => handleChange("surname", e.target.value)}
            className="px-2 py-1 text-sm border rounded"
          />
        </div> */}

        {/* <input
          type="date"
          value={form.dateOfBirth}
          onChange={(e) => handleChange("dateOfBirth", e.target.value)}
          className="w-full px-2 py-1 text-sm border rounded"
        /> */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <input
              placeholder="Forename *"
              type="text"
              value={form.forename}
              onChange={(e) => handleChange("forename", e.target.value)}
              className={`w-full border rounded px-2 py-1 text-sm ${
                fieldErrors.forename ? "border-red-500" : ""
              }`}
            />
            {fieldErrors.forename && (
              <p className="text-xs text-red-500">{fieldErrors.forename}</p>
            )}
          </div>
          <div className="space-y-1">
            <input
              placeholder="Surname *"
              value={form.surname}
              onChange={(e) => handleChange("surname", e.target.value)}
              className={`w-full border rounded px-2 py-1 text-sm ${
                fieldErrors.surname ? "border-red-500" : ""
              }`}
            />
            {fieldErrors.surname && (
              <p className="text-xs text-red-500">{fieldErrors.surname}</p>
            )}
          </div>
        </div>
        <div className="space-y-1">
          <input
            type="date"
            value={form.dateOfBirth}
            onChange={(e) => handleChange("dateOfBirth", e.target.value)}
            className={`w-full border rounded px-2 py-1 text-sm ${
              fieldErrors.dateOfBirth ? "border-red-500" : ""
            }`}
          />
          {fieldErrors.dateOfBirth && (
            <p className="text-xs text-red-500">{fieldErrors.dateOfBirth}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <input
            placeholder="Email"
            value={form.email}
            onChange={(e) => handleChange("email", e.target.value)}
            className="px-2 py-1 text-sm border rounded"
          />
          <input
            placeholder="Telephone"
            value={form.telephone}
            onChange={(e) => handleChange("telephone", e.target.value)}
            className="px-2 py-1 text-sm border rounded"
          />
        </div>

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-1 text-sm border rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={handleSubmit}
            className="flex-1 py-1 text-sm text-white bg-blue-600 rounded"
          >
            {isSaving ? "Saving..." : "Add Patient"}
          </button>
        </div>
      </div>
    );
  }
  const handleAddPatient = (query?: string) => {
    if (query) {
      const parts = query.trim().split(" ");
      const first = parts[0] || "";
      const last = parts.slice(1).join(" ") || "";

      setQuickAddDefaults({
        forename: first,
        surname: last,
        dateOfBirth: "",
        email: "",
        telephone: "",
      });
    }

    setShowQuickAddPatient(true);
  };
  // Add these helper functions inside your BookingModal component
  const calculateMatchScore = (
    localPatient: any,
    sourcePatient: any,
  ): number => {
    if (!sourcePatient) return 0;

    let score = 0;
    const weights = {
      name: 35,
      dob: 30,
      email: 15,
      phone: 10,
      postcode: 10,
    };

    // Name match (compare forename and surname separately for better accuracy)
    const localForename = (localPatient.foreName || "").toLowerCase().trim();
    const localSurname = (localPatient.surName || "").toLowerCase().trim();
    const sourceForename = (
      sourcePatient.foreName ||
      sourcePatient.patientName?.split(" ")[0] ||
      ""
    )
      .toLowerCase()
      .trim();
    const sourceSurname = (
      sourcePatient.surName ||
      sourcePatient.patientName?.split(" ").slice(1).join(" ") ||
      ""
    )
      .toLowerCase()
      .trim();

    if (localForename === sourceForename && localSurname === sourceSurname) {
      score += weights.name; // Exact full name match
    } else if (localForename === sourceForename) {
      score += weights.name * 0.6; // First name match only
    } else if (localSurname === sourceSurname) {
      score += weights.name * 0.4; // Last name match only
    } else {
      // Partial name matching
      const localFullName = `${localForename} ${localSurname}`;
      const sourceFullName = `${sourceForename} ${sourceSurname}`;
      if (
        localFullName.includes(sourceForename) ||
        sourceFullName.includes(localForename)
      ) {
        score += weights.name * 0.3;
      }
    }

    // DOB match
    if (localPatient.dob && sourcePatient.dob) {
      const localDob = new Date(localPatient.dob).toDateString();
      const sourceDob = new Date(sourcePatient.dob).toDateString();
      if (localDob === sourceDob) {
        score += weights.dob;
      } else {
        // Check if year and month match (partial DOB match)
        const localDate = new Date(localPatient.dob);
        const sourceDate = new Date(sourcePatient.dob);
        if (
          localDate.getFullYear() === sourceDate.getFullYear() &&
          localDate.getMonth() === sourceDate.getMonth()
        ) {
          score += weights.dob * 0.5;
        }
      }
    }

    // Email match
    if (localPatient.email && sourcePatient.email) {
      if (
        localPatient.email.toLowerCase() === sourcePatient.email.toLowerCase()
      ) {
        score += weights.email;
      }
    }

    // Phone match
    if (
      localPatient.contacts?.length > 0 &&
      sourcePatient.contacts?.length > 0
    ) {
      const localPhone = localPatient.contacts
        .find((c: any) => c.contactType === 4)
        ?.value?.replace(/\D/g, "");
      const sourcePhone = sourcePatient.contacts
        .find((c: any) => c.contactType === 4)
        ?.value?.replace(/\D/g, "");

      if (localPhone && sourcePhone && localPhone === sourcePhone) {
        score += weights.phone;
      }
    }

    // Postcode match
    const extractPostcode = (address: string) => {
      const postcodeMatch = address?.match(
        /[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}/i,
      );
      return postcodeMatch
        ? postcodeMatch[0].replace(/\s/g, "").toUpperCase()
        : "";
    };

    const localPostcode = extractPostcode(localPatient.address || "");
    const sourcePostcode = extractPostcode(sourcePatient.address || "");

    if (localPostcode && sourcePostcode && localPostcode === sourcePostcode) {
      score += weights.postcode;
    } else if (
      localPostcode &&
      sourcePostcode &&
      localPostcode.substring(0, 3) === sourcePostcode.substring(0, 3)
    ) {
      score += weights.postcode * 0.5; // Partial postcode match (first part)
    }

    return Math.min(100, Math.round(score));
  };

  const generateMatchReasons = (
    localPatient: any,
    sourcePatient: any,
  ): string[] => {
    const reasons: string[] = [];

    if (!sourcePatient) return reasons;

    const localForename = (localPatient.foreName || "").toLowerCase().trim();
    const localSurname = (localPatient.surName || "").toLowerCase().trim();
    const sourceForename = (
      sourcePatient.foreName ||
      sourcePatient.patientName?.split(" ")[0] ||
      ""
    )
      .toLowerCase()
      .trim();
    const sourceSurname = (
      sourcePatient.surName ||
      sourcePatient.patientName?.split(" ").slice(1).join(" ") ||
      ""
    )
      .toLowerCase()
      .trim();

    if (localForename === sourceForename && localSurname === sourceSurname) {
      reasons.push("Exact name match");
    } else if (localForename === sourceForename) {
      reasons.push("First name matches");
    } else if (localSurname === sourceSurname) {
      reasons.push("Last name matches");
    }

    if (localPatient.dob && sourcePatient.dob) {
      const localDob = new Date(localPatient.dob).toDateString();
      const sourceDob = new Date(sourcePatient.dob).toDateString();
      if (localDob === sourceDob) {
        reasons.push("Date of birth matches");
      }
    }

    if (localPatient.email && sourcePatient.email) {
      if (
        localPatient.email.toLowerCase() === sourcePatient.email.toLowerCase()
      ) {
        reasons.push("Email matches");
      }
    }

    if (
      localPatient.contacts?.length > 0 &&
      sourcePatient.contacts?.length > 0
    ) {
      const localPhone = localPatient.contacts.find(
        (c: any) => c.contactType === 4,
      )?.value;
      const sourcePhone = sourcePatient.contacts.find(
        (c: any) => c.contactType === 4,
      )?.value;

      if (localPhone && sourcePhone && localPhone === sourcePhone) {
        reasons.push("Phone number matches");
      }
    }

    const extractPostcode = (address: string) => {
      const postcodeMatch = address?.match(
        /[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}/i,
      );
      return postcodeMatch ? postcodeMatch[0] : "";
    };

    const localPostcode = extractPostcode(localPatient.address || "");
    const sourcePostcode = extractPostcode(sourcePatient.address || "");

    if (localPostcode && sourcePostcode && localPostcode === sourcePostcode) {
      reasons.push("Postcode matches");
    }

    return reasons;
  };
  const handleSavePatient = async (values) => {
    const finalPayload = {
      patientID: uuidv4(),
      foreName: values.forename || "",
      surName: values.surname || "",
      dob: values.dateOfBirth ? new Date(values.dateOfBirth).toISOString() : "",
      contacts: values.telephone
        ? [
            {
              patientContactId: uuidv4(),
              contactType: 4,
              value: values.telephone,
            },
          ]
        : [],
      email: values.email || "",
      companyID: companyId,
      pharmacyID: activePharmacyId,
      createdOn: new Date(),
    };

    try {
      const result = await addNewPatientMatch(finalPayload);

      const message = result?.message;
      const patientDetailsList = result?.patientDetailsModel || [];
      const addedPatient = result?.addedPatient;

      if (message !== "Patient added successfully.") {
        setShowQuickAddPatient(true);
        return;
      }

      // Calculate if there are partial matches
      const hasPartialMatches = patientDetailsList.length > 1;

      if (addedPatient) {
        // Store ALL matching patients
        setLocalMatchingPatients(patientDetailsList);
        setLocalPartialMatch(hasPartialMatches);

        const newPatient = {
          patientID: addedPatient.patientID || finalPayload.patientID,
          patientName: `${
            addedPatient.foreName || values.forename
          } ${addedPatient.surName || values.surname}`.trim(),
          email: addedPatient.email || values.email || "",
          contacts:
            addedPatient.contacts?.length > 0
              ? addedPatient.contacts
              : values.telephone
                ? [{ contactType: 4, value: values.telephone }]
                : [],
        };

        // Set form values
        setValue("patientName", newPatient.patientName, {
          shouldValidate: true,
          shouldDirty: true,
        });

        setValue("patientId", String(newPatient.patientID), {
          shouldValidate: true,
          shouldDirty: true,
        });

        if (newPatient.email) {
          setValue("email", newPatient.email, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }

        if (newPatient.contacts?.length > 0) {
          const mobileContact = newPatient.contacts.find(
            (c) => c.contactType === 4,
          );
          if (mobileContact?.value) {
            setValue("mobile", mobileContact.value, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
        }

        // Set the search query to the new patient name
        setPatientSearchQuery(newPatient.patientName);

        // Close the quick add form
        setShowQuickAddPatient(false);

        // Add to cached patients
        setCachedPatients((prev) => {
          const exists = prev.some((p) => p.patientID === newPatient.patientID);
          if (!exists) {
            return [...prev, newPatient];
          }
          return prev;
        });
      }
    } catch (error) {
      console.error("Error while adding patient:", error);
    }
  };

  const [cachedPatients, setCachedPatients] = React.useState<any[]>([]);
  const [lastApiKeyword, setLastApiKeyword] = React.useState("");

  const isConsultationCompleted =
    selectedPatientConsultation?.consultationStatus === 1;
  const isCancelledAppointment = (currentBooking as any)?.eventStatus === 4;

  // Sort patients by relevance
  const sortedPatients = React.useMemo(() => {
    return sortPatientsByRelevance(filteredPatients || [], patientSearchQuery);
  }, [filteredPatients, patientSearchQuery]);
  const hasConsultationPermission = checkUserPermissions(
    accessTitanverseConsultationUpsert,
  );

  // load company vaccine catalog when modal opens
  React.useEffect(() => {
    if (open && companyId) {
      clearDefaultCalenderAppointmentPharmacyServices();
      clearServiceUsersByCompany();
      getCompanyVaccineByCompanyId(companyId).catch((err) => {
        console.error("Error fetching company vaccines", err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyId]);

  // Map the scheduler's lightweight service data to the dropdown shape
  const mappedServices = React.useMemo(() => {
    const services = bookingModalServices;

    const mapped = services
      .filter(
        (s: any) => s.isActive && s.serviceType !== 1 && s.serviceType !== 3,
      )
      .map((s: any) => ({
        _id: s._id,
        name: s.serviceName,
        serviceType: s.serviceType,
        serviceDuration: s.serviceDuration,
        isRoomRequired: s.isRoomRequired,
        serviceUnavailableOnDay: s.serviceUnavailableOnDay,
        serviceStartDate: s.serviceStartDate,
        serviceEndDate: s.serviceEndDate,
      }));

    if (isEditMode && currentBooking) {
      const bookingServiceId = String(
        (currentBooking as any).serviceId ||
          (currentBooking as any).serviceType ||
          "",
      );
      const alreadyIncluded = mapped.some(
        (s: any) => String(s._id) === bookingServiceId,
      );
      if (bookingServiceId && !alreadyIncluded) {
        const rawService = (services as any[]).find(
          (s: any) => String(s?._id) === bookingServiceId,
        );
        mapped.push({
          _id: bookingServiceId,
          name:
            rawService?.serviceName ||
            (currentBooking as any).serviceName ||
            "Selected Service",
          serviceType: rawService?.serviceType,
          serviceDuration:
            rawService?.serviceDuration || (currentBooking as any).duration,
          isRoomRequired: rawService?.isRoomRequired,
          serviceUnavailableOnDay: rawService?.serviceUnavailableOnDay,
          serviceStartDate: rawService?.serviceStartDate,
          serviceEndDate: rawService?.serviceEndDate,
        });
      }
    }

    return mapped;
  }, [
    bookingModalServices,
    isEditMode,
    currentBooking?.id,
    (currentBooking as any)?.serviceId,
    (currentBooking as any)?.serviceType,
    (currentBooking as any)?.serviceName,
    (currentBooking as any)?.duration,
  ]);

  const findServiceById = React.useCallback(
    (id: string) => {
      if (!id) return undefined;
      const targetId = String(id).trim().toLowerCase();
      return (
        (mappedServices || []).find(
          (s: any) =>
            String(s?._id || "")
              .trim()
              .toLowerCase() === targetId,
        ) ||
        (bookingModalServices || []).find(
          (s: any) =>
            String(s?._id || s?.id || "")
              .trim()
              .toLowerCase() === targetId,
        ) ||
        (defaultPharmacyServices || []).find(
          (s: any) =>
            String(s?._id || s?.id || "")
              .trim()
              .toLowerCase() === targetId,
        ) ||
        (serviceTypesInfo || []).find(
          (s: any) =>
            String(s?._id || s?.id || "")
              .trim()
              .toLowerCase() === targetId,
        )
      );
    },
    [
      mappedServices,
      bookingModalServices,
      defaultPharmacyServices,
      serviceTypesInfo,
    ],
  );

  const getDurationOptionsForService = React.useCallback((service: any) => {
    const base = parseInt(String(service?.serviceDuration ?? 0), 10) || 0;
    const values =
      base > 0
        ? Array.from({ length: 5 }, (_, index) => base * (index + 1))
        : [15, 20, 30, 45, 60, 90, 120];

    return values.map((value) => String(value));
  }, []);

  const getInitialDurationForService = React.useCallback(
    (service: any) => {
      if (!service) return "15";
      const base = parseInt(String(service?.serviceDuration ?? 0), 10) || 0;
      return base > 0
        ? String(base)
        : getDurationOptionsForService(service)[0] || "15";
    },
    [getDurationOptionsForService],
  );

  // Dynamic schema to require room when the selected service mandates it
  const bookingSchema = React.useMemo(() => {
    return z
      .object({
        patientName: z.string().min(1, "Patient name is required"),
        patientId: z.string().optional(),
        pharmacist: z.string().min(1, "Pharmacist is required"),
        serviceType: z.string().min(1, "Service type is required"),
        date: z.string().min(1, "Date is required"),
        time: z.string().min(1, "Time is required"),
        duration: z.string().min(1, "Duration is required"),
        room: z.string().optional(),
        notes: z
          .string()
          .max(NOTES_MAX_LENGTH, charLimitMessage(NOTES_MAX_LENGTH))
          .optional(),
        email: z
          .string()
          .trim()
          .max(254)
          .email("Invalid email address")
          .optional()
          .or(z.literal("")),
        mobile: z
          .string()
          .trim()
          .max(20)
          .refine(
            (val) => {
              if (!val) return true;
              const digitsOnly = val.replace(/\D/g, "");
              const validChars = /^\+?[0-9\s()\-]+$/.test(val);
              return (
                validChars && digitsOnly.length >= 7 && digitsOnly.length <= 15
              );
            },
            {
              message: "Please enter a valid phone number (at least 7 digits)",
            },
          )
          .optional()
          .or(z.literal("")),
        confirmationMethod: z
          .union([z.enum(["email", "mobile"]), z.literal(""), z.undefined()])
          .transform((v) => (v === "" ? undefined : v))
          .optional(),
      })
      .superRefine((data, ctx) => {
        const svc = (mappedServices || []).find(
          (s: any) => s?._id === data.serviceType,
        );
        if (svc && (svc as any).isRoomRequired) {
          const v = (data.room || "").trim();
          if (!v) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["room"],
              message: "Room is required for this service",
            });
          }
        }
        if (data.confirmationMethod === "email") {
          const v = (data.email || "").trim();
          if (!v) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["email"],
              message: "Email is required for email confirmation",
            });
          }
        }
        if (data.confirmationMethod === "mobile") {
          const v = (data.mobile || "").trim();
          if (!v) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["mobile"],
              message: "Mobile is required for SMS confirmation",
            });
          }
        }
      });
  }, [mappedServices]);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<BookingForm>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      duration: "",
      date: initialDate || "",
      time: initialTime || "",
      serviceType: initialServiceTypeId || "",
      email: "",
      mobile: "",
      confirmationMethod: undefined,
    },
  });

  // True when every pharmacist is booked at the slot, per the remembered
  // get-active-pharmacists result or the appointments loaded on the calendar.
  // Takes date/duration explicitly so handlers don't read stale render values.
  const isSlotFullyBookedFor = React.useCallback(
    (date: string, time: string, duration: string | number) => {
      const svc = getValues("serviceType");
      if (fullyBookedTimes.includes(`${svc}|${date}|${duration}|${time}`))
        return true;
      if (
        isEditMode ||
        !isPharmacistBookedAtSlot ||
        !svc ||
        !date ||
        !servicePharmacists.length ||
        !Number(duration)
      )
        return false;
      return servicePharmacists.every((p: any) =>
        isPharmacistBookedAtSlot(date, time, Number(duration), {
          id: p?.id,
          name: p?.name,
        }),
      );
    },
    [
      fullyBookedTimes,
      isEditMode,
      isPharmacistBookedAtSlot,
      servicePharmacists,
      getValues,
    ],
  );

  const getBookingFormData = React.useCallback(() => {
    const values = getValues();
    return {
      ...values,
      isNewAppointment: !isEditMode,
      ...(isEditMode ? { otherPharmacyId: activePharmacyId } : {}),
      eventId:
        isEditMode && currentBooking?.id ? String(currentBooking.id) : "",
      isSlotUnavailable: (time: string) =>
        isSlotFullyBookedFor(values.date, time, values.duration),
      // Lets the first-slot pick match what the Time dropdown actually lists.
      isSlotSelectable: (time: string) => {
        if (showAllServiceTimes) return true;
        const grid = getPharmacistWeekAvailabilitySlots?.(
          values.date,
          values.pharmacist,
          Number(values.duration) || 0,
        );
        return !grid || grid.includes(time);
      },
    };
  }, [
    currentBooking,
    getValues,
    isEditMode,
    activePharmacyId,
    isSlotFullyBookedFor,
    showAllServiceTimes,
    getPharmacistWeekAvailabilitySlots,
  ]);

  const handlePatientSearch = React.useCallback(
    async (query: string) => {
      // Update state first
      setPatientSearchQuery(query);

      // Clear if empty
      if (query.length === 0) {
        setShowPatientResults(false);
        return;
      }

      const normalizedQuery = query.trim().toLowerCase();

      if (
        isEditMode ||
        !normalizedQuery ||
        normalizedQuery.length < 2 ||
        !activePharmacyId
      ) {
        return;
      }

      // Check if we need to hit the API
      const shouldHitApi =
        !lastApiKeyword || !normalizedQuery.startsWith(lastApiKeyword);

      if (shouldHitApi) {
        setPatientSearchLoading(true);
        try {
          const payload = {
            keyword: normalizedQuery,
            practitioners: "",
            treatmentPlan: "",
            isTitanSearch: false,
            consultationStatus: 0,
            pharmacyID: activePharmacyId,
            companyId: companyId,
          };

          await getPatientsListPowerSearch(payload);
          setLastApiKeyword(normalizedQuery);
          setShowPatientResults(true);
        } catch (error) {
          console.error("Failed to fetch patients:", error);
        } finally {
          setPatientSearchLoading(false);
        }
        return;
      }

      // Local filtering for substrings
      const locallyFiltered = cachedPatients.filter((p) =>
        [p.patientName, p.nhsNumber, p.address]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(normalizedQuery)),
      );

      setFilteredPatients(locallyFiltered);
      setShowPatientResults(true);
    },
    [activePharmacyId, isEditMode, lastApiKeyword, cachedPatients],
  );

  const handlePatientSelect = React.useCallback(
    (patient: any) => {
      if (isEditMode || !patient) return;

      if (patient && patient.patientName) {
        setValue("patientName", patient.patientName, {
          shouldValidate: true,
          shouldDirty: true,
        });
        // Always overwrite so a previous patient's ID can never survive a
        // selection (some results carry the ID only in _id).
        setValue(
          "patientId",
          String(patient.patientID || patient.patientId || patient._id || ""),
          {
            shouldValidate: true,
            shouldDirty: true,
          },
        );
        const email = patient?.email || "";
        const contacts = Array.isArray(patient?.contacts)
          ? patient.contacts
          : [];
        const mobile =
          contacts.find((c: any) => String(c?.contactType) === "4" && c?.value)
            ?.value ||
          contacts.find((c: any) => String(c?.contactType) === "2" && c?.value)
            ?.value ||
          "";
        setValue("email", email, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("mobile", mobile, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      setPatientSearchQuery(patient.patientName);
      setShowPatientResults(false);
      if (patientInputRef.current) {
        patientInputRef.current.blur();
      }
      if (
        typeof document !== "undefined" &&
        document.activeElement instanceof HTMLElement
      ) {
        document.activeElement.blur();
      }
    },
    [isEditMode, setValue],
  );

  // Sync patient search query with form value
  React.useEffect(() => {
    const patientName = watch("patientName") || "";
    setPatientSearchQuery(patientName);
  }, [watch("patientName")]);

  // Cache filtered patients
  React.useEffect(() => {
    if (filteredPatients && filteredPatients.length > 0) {
      setCachedPatients(filteredPatients);
    }
  }, [filteredPatients]);

  // Reset patient search when pharmacy changes
  React.useEffect(() => {
    setCachedPatients([]);
    setLastApiKeyword("");
    setFilteredPatients([]);
  }, [currentPharmacyId]);

  // Bind rooms returned from API endpoint (serviceRooms) based on service type & time selection
  const allAvailableRooms = React.useMemo(() => {
    const list: { id: string; name: string }[] = [];
    const seen = new Set<string>();

    const addRoom = (id: string | number, name: string) => {
      if (!id && !name) return;
      const idStr = String(id || name);
      const nameStr = String(name || id);
      const key = idStr.toLowerCase().trim();
      if (!seen.has(key)) {
        seen.add(key);
        list.push({ id: idStr, name: nameStr });
      }
    };

    // 1. Add serviceRooms (returned from the get-rooms-for-appointment API response)
    if (Array.isArray(serviceRooms)) {
      serviceRooms.forEach((r: any) => {
        const rId = r.id || r._id || r.roomId;
        const rName = r.name || r.roomName || r.nameStr || rId;
        if (rId || rName) {
          addRoom(rId, rName);
        }
      });
    }

    // 2. In edit mode, ensure current booking's room is present so prefilled room displays
    if (currentBooking) {
      const b = currentBooking as any;
      const rawRoomVal = b.roomId || b.RoomId || b.room;
      if (rawRoomVal) {
        const rawStr = String(rawRoomVal).trim();
        const matched =
          (serviceRooms as any[])?.find(
            (r: any) =>
              String(r.id || r._id || r.roomId).toLowerCase() ===
                rawStr.toLowerCase() ||
              String(r.name || r.roomName || "")
                .toLowerCase()
                .trim() === rawStr.toLowerCase(),
          ) ||
          (pharmacyRooms as any[])?.find(
            (r: any) =>
              String(r.id || r._id).toLowerCase() === rawStr.toLowerCase() ||
              String(r.name || "")
                .toLowerCase()
                .trim() === rawStr.toLowerCase(),
          );

        if (matched) {
          addRoom(
            matched.id || matched._id || matched.roomId,
            matched.name || matched.roomName,
          );
        } else if (!rawStr.match(/^[0-9a-fA-F]{24}$/)) {
          const rName =
            b.roomName ||
            (b.room && !String(b.room).match(/^[0-9a-fA-F]{24}$/)
              ? b.room
              : rawStr);
          addRoom(rawStr, rName);
        }
      }
    }

    return list;
  }, [serviceRooms, currentBooking, pharmacyRooms]);

  // Helper to resolve room ID from booking data matching against allAvailableRooms / pharmacyRooms / serviceRooms
  const resolveRoomId = React.useCallback(
    (bookingData: any): string => {
      if (!bookingData) return "";
      const rawVal =
        bookingData.roomId || bookingData.RoomId || bookingData.room || "";
      if (!rawVal) return "";

      const rawStr = String(rawVal).trim();

      // 1. Try to find match by id/_id in allAvailableRooms
      const matchById = allAvailableRooms.find(
        (r) => String(r.id).toLowerCase() === rawStr.toLowerCase(),
      );
      if (matchById) return matchById.id;

      // 2. Try to find match by name in allAvailableRooms
      const matchByName = allAvailableRooms.find(
        (r) => String(r.name).toLowerCase().trim() === rawStr.toLowerCase(),
      );
      if (matchByName) return matchByName.id;

      // 3. Fallback to pharmacyRooms / serviceRooms
      const pharmMatch =
        (pharmacyRooms as any[])?.find(
          (r: any) =>
            String(r.id || r._id).toLowerCase() === rawStr.toLowerCase() ||
            String(r.name || "")
              .toLowerCase()
              .trim() === rawStr.toLowerCase(),
        ) ||
        (serviceRooms as any[])?.find(
          (r: any) =>
            String(r.id || r._id).toLowerCase() === rawStr.toLowerCase() ||
            String(r.name || "")
              .toLowerCase()
              .trim() === rawStr.toLowerCase(),
        );

      if (pharmMatch) {
        return String(pharmMatch.id || pharmMatch._id);
      }

      if (rawStr.match(/^[0-9a-fA-F]{24}$/)) {
        return "";
      }

      return rawStr;
    },
    [allAvailableRooms, pharmacyRooms, serviceRooms],
  );

  const pharmacistPrefillPending = React.useRef<"loading" | "ready" | false>(
    false,
  );
  const [pharmacistPrefillLoadVersion, setPharmacistPrefillLoadVersion] =
    React.useState(0);
  const prefillPharmacistValue = watch("pharmacist") || "";
  const lastResetKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      lastResetKeyRef.current = null;
      return;
    }

    const currentKey = currentBooking
      ? `edit-${
          currentBooking.id ||
          (currentBooking as any).consultationID ||
          "current"
        }`
      : `create-${initialPatientId || ""}-${
          initialPatientName || ""
        }-${initialDate || ""}-${initialTime || ""}`;

    if (lastResetKeyRef.current === currentKey) {
      return;
    }
    lastResetKeyRef.current = currentKey;

    pharmacistPrefillPending.current = currentBooking ? "loading" : false;
    if (currentBooking) {
      const initialPName =
        (currentBooking as any).patientName ||
        (currentBooking as any).patientID_name ||
        initialPatientName ||
        "";
      const resolvedRoomId = resolveRoomId(currentBooking);

      reset({
        patientName: initialPName,
        patientId:
          (currentBooking as any).patientId ||
          (currentBooking as any).patientID ||
          "",
        pharmacist: "",
        serviceType:
          (currentBooking as any).serviceId ||
          (currentBooking as any).serviceType ||
          "",
        date: currentBooking.date,
        time: currentBooking.time,
        duration: String(currentBooking.duration || ""),
        room: resolvedRoomId,
        notes: (currentBooking as any).notes || "",
        email: (currentBooking as any).email || "",
        mobile:
          (currentBooking as any).contactNumber ||
          (currentBooking as any).mobile ||
          "",
        confirmationMethod:
          (currentBooking as any).confirmationMethod || undefined,
      });

      setPatientSearchQuery(initialPName);
    } else {
      const nextDate = initialDate || slotSelectedDate || "";
      const nextTime = initialTime || "";
      const svcId = initialServiceTypeId || "";
      const matchedSvc = findServiceById(svcId);
      const resolvedDuration =
        (initialDuration ? String(initialDuration) : "") ||
        (matchedSvc ? getInitialDurationForService(matchedSvc) : "") ||
        "15";

      reset({
        duration: resolvedDuration,
        date: nextDate,
        time: nextTime,
        patientName: initialPatientName || "",
        patientId: initialPatientId || "",
        pharmacist: "",
        serviceType: svcId,
        room: "",
        notes: "",
        email: initialPatientEmail || "",
        mobile: initialPatientPhone || "",
        confirmationMethod: undefined,
      });

      setPatientSearchQuery(initialPatientName || "");

      setLocalTasks([]);
      setLocalVaccines([]);
      setInternalTaskLabel("");
      setInternalVaccineId("");
    }
  }, [
    open,
    currentBooking?.id,
    initialDate,
    initialTime,
    initialDuration,
    initialServiceTypeId,
    initialPharmacistName,
    initialPatientName,
    initialPatientId,
    initialPatientEmail,
    initialPatientPhone,
    slotSelectedDate,
    findServiceById,
    getInitialDurationForService,
    reset,
  ]);

  React.useEffect(() => {
    if (!open || !currentBooking || servicePharmacists.length === 0) {
      return;
    }

    if (getValues("pharmacist")) {
      pharmacistPrefillPending.current = false;
      return;
    }

    const bookingPharmacistId = String(
      (currentBooking as any).resourceId ||
        (currentBooking as any).ResourceId ||
        (currentBooking as any).pharmacistId ||
        (currentBooking as any).pharmacist ||
        (currentBooking as any).userId ||
        "",
    )
      .trim()
      .toLowerCase();
    const bookingPharmacistName = String(
      (currentBooking as any).pharmacistName ||
        (currentBooking as any).pharmacist ||
        (currentBooking as any).resourceName ||
        initialPharmacistName ||
        "",
    )
      .trim()
      .toLowerCase();

    const match =
      (bookingPharmacistId &&
        servicePharmacists.find(
          (pharmacist: any) =>
            String(pharmacist.id || "")
              .trim()
              .toLowerCase() === bookingPharmacistId ||
            String(pharmacist.userId || "")
              .trim()
              .toLowerCase() === bookingPharmacistId ||
            String(pharmacist.emailAddress || "")
              .trim()
              .toLowerCase() === bookingPharmacistId ||
            String(pharmacist.name || "")
              .trim()
              .toLowerCase() === bookingPharmacistId ||
            String(pharmacist.fullName || "")
              .trim()
              .toLowerCase() === bookingPharmacistId,
        )) ||
      (bookingPharmacistName &&
        servicePharmacists.find(
          (pharmacist: any) =>
            String(pharmacist.name || "")
              .trim()
              .toLowerCase() === bookingPharmacistName ||
            String(pharmacist.fullName || "")
              .trim()
              .toLowerCase() === bookingPharmacistName ||
            String(pharmacist.id || "")
              .trim()
              .toLowerCase() === bookingPharmacistName ||
            String(pharmacist.emailAddress || "")
              .trim()
              .toLowerCase() === bookingPharmacistName,
        ));

    if (!match?.id) return;

    pharmacistPrefillPending.current = false;
    const matchedId = String(match.id);
    setValue("pharmacist", matchedId, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true,
    });

    // Still fetch this pharmacist's own week availability even in calendar-edit
    // mode - without it pharmacistWeekSlots stays null and the Time select
    // falls back to showing every pharmacist's slots instead of splitting
    // this pharmacist's own hours from ad-hoc ones.
    const serviceId = getValues("serviceType");
    if (serviceId && onPharmacistSelected) {
      onPharmacistSelected(serviceId, matchedId, activePharmacyId)
        .then(async () => {
          try {
            setIsRoomsLoading(true);
            await handleGetAppointmentRooms?.({
              ...getBookingFormData(),
              otherPharmacyId: activePharmacyId,
              PharmacyID: activePharmacyId,
              OtherPharmacyId: activePharmacyId,
            });
          } finally {
            setIsRoomsLoading(false);
          }
        })
        .catch(() => {});
    }
  }, [
    open,
    currentBooking?.id,
    servicePharmacists,
    prefillPharmacistValue,
    pharmacistPrefillLoadVersion,
    isCalendarEdit,
    isEditMode,
    onPharmacistSelected,
    activePharmacyId,
    initialPharmacistName,
    getValues,
    setValue,
  ]);

  // Ensure room ID is bound when rooms are loaded or updated
  React.useEffect(() => {
    if (open && currentBooking) {
      const targetRoomId = resolveRoomId(currentBooking);
      const currentRoomInForm = getValues("room");
      if (!currentRoomInForm && targetRoomId) {
        setValue("room", targetRoomId, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: true,
        });
      }
    }
  }, [open, currentBooking?.id, resolveRoomId, setValue, getValues]);

  // Ensure pharmacist list is fetched when a service is already set (e.g., edit mode)
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const loadInitialBookingData = async () => {
      const formData = getValues();
      const serviceTypeValue =
        formData.serviceType ||
        (currentBooking as any)?.serviceId ||
        (currentBooking as any)?.serviceType ||
        (currentBooking as any)?.serviceID ||
        initialServiceTypeId ||
        "";
      const dateValue =
        formData.date || currentBooking?.date || initialDate || "";
      const timeValue =
        formData.time || currentBooking?.time || initialTime || "";
      const matchedSvc = findServiceById(serviceTypeValue);
      const durationValue =
        formData.duration ||
        String(currentBooking?.duration || "") ||
        (initialDuration ? String(initialDuration) : "") ||
        (matchedSvc ? getInitialDurationForService(matchedSvc) : "") ||
        "";

      if (!serviceTypeValue) return;

      resetServiceUsersFetchGuard?.();

      const bookingData = {
        ...getBookingFormData(),
        serviceType: serviceTypeValue,
        date: dateValue,
        time: timeValue,
        duration: durationValue,
      };

      try {
        await onServiceTypeSelected?.(bookingData);
      } catch (e) {
        console.error(
          "Error in onServiceTypeSelected during loadInitialBookingData:",
          e,
        );
      } finally {
        if (!cancelled) {
          pharmacistPrefillPending.current = "ready";
          setPharmacistPrefillLoadVersion((version) => version + 1);
        }
      }
      if (cancelled) return;
      await handleGetAppointmentRooms?.(bookingData);
    };

    loadInitialBookingData().catch(() => {});
    return () => {
      cancelled = true;
    };
    // findServiceById/getInitialDurationForService are intentionally excluded:
    // they get a new identity whenever bookingModalServices refreshes (e.g.
    // right after picking a date triggers a date-specific services fetch),
    // which would otherwise re-run this effect a second time and reset the
    // onServiceTypeSelected/getAppointmentRooms dedup guards, causing the
    // same APIs to be called twice for a single date selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    currentBooking?.id,
    initialServiceTypeId,
    initialDate,
    initialTime,
    initialDuration,
    activePharmacyId,
    isCalendarEdit,
    isEditMode,
    resetServiceUsersFetchGuard,
  ]);

  React.useEffect(() => {
    const rawPid =
      (currentBooking as any)?.patientId ||
      (currentBooking as any)?.patientID ||
      initialPatientId;
    if (open && (currentBooking || isEditMode) && rawPid) {
      const pid = String(rawPid);
      getPatientByPatientId(pid)
        .then((res: any) => {
          const patient = (res && (res.patient || res)) || {};
          const name =
            (patient as any).patientName ||
            `${(patient as any).foreName || ""} ${
              (patient as any).surName || ""
            }`.trim() ||
            res?.patientName ||
            "";
          if (name) {
            setValue("patientName", name, { shouldValidate: true });
            setPatientSearchQuery(name);
          }

          const emailFromPatient = (patient as any)?.email || res?.email || "";
          const contacts = Array.isArray((patient as any)?.contacts)
            ? (patient as any).contacts
            : Array.isArray((res as any)?.contacts)
              ? (res as any).contacts
              : [];
          const mobileFromPatient =
            contacts.find(
              (c: any) => String(c?.contactType) === "4" && c?.value,
            )?.value ||
            contacts.find(
              (c: any) => String(c?.contactType) === "2" && c?.value,
            )?.value ||
            "";

          const currentEmail = (getValues("email") || "").trim();
          const currentMobile = (getValues("mobile") || "").trim();

          if (!currentEmail && emailFromPatient) {
            setValue("email", emailFromPatient, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
          if (!currentMobile && mobileFromPatient) {
            setValue("mobile", mobileFromPatient, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentBooking?.id, initialPatientId]);
  React.useEffect(() => {
    if (!open) return;
    // Only load from consultation actions if editing existing booking
    if (!isEditMode || !currentBooking) return;
    const actions = selectedPatientConsultation?.actions;
    if (!Array.isArray(actions) || actions.length === 0) return;

    const tasks: AppointmentAction[] = [];
    const vaccines: AppointmentVaccine[] = [];

    actions.forEach((action) => {
      try {
        const payload =
          typeof action.payload === "string"
            ? JSON.parse(action.payload)
            : action.payload;

        if (action?.actionNumber === "14" && Array.isArray(payload?.tasks)) {
          payload.tasks.forEach((t: any) => {
            tasks.push({
              id: uuidv4(),
              label: t.label || "",
              completed: t.completed ?? false,
            });
          });
        }

        if (
          action?.actionNumber === "12" &&
          Array.isArray(payload?.vaccineItems)
        ) {
          payload.vaccineItems.forEach((v: any) => {
            vaccines.push({
              id: uuidv4(),
              name: v.vaccineName || "",
              vaccineName: v.vaccineName || "",
              dose: v.schedule || "",
              notes: `${v.route || ""} | ${v.site || ""}`,
            });
          });
        }
      } catch (e) {
        console.error("action parse error", e);
      }
    });

    if (tasks.length > 0) setLocalTasks(tasks);
    if (vaccines.length > 0) setLocalVaccines(vaccines);
  }, [open, selectedPatientConsultation, isEditMode, currentBooking]);
  // Prefill patient from initial props (e.g., from appointment)
  React.useEffect(() => {
    if (!open) return;
    if (isEditMode) return; // Only for new appointments

    // Set patient name
    if (initialPatientName) {
      const currentPatientName = (watch("patientName") || "").trim();
      if (!currentPatientName) {
        setValue("patientName", initialPatientName, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setPatientSearchQuery(initialPatientName);
      }
    }

    if (initialPatientId) {
      const currentPatientId = (watch("patientId") || "").trim();
      if (!currentPatientId) {
        setValue("patientId", initialPatientId, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }

    if (initialPatientEmail) {
      const currentEmail = (watch("email") || "").trim();
      if (!currentEmail) {
        setValue("email", initialPatientEmail, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }

    if (initialPatientPhone) {
      const currentMobile = (watch("mobile") || "").trim();
      if (!currentMobile) {
        setValue("mobile", initialPatientPhone, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [open, isEditMode, initialPatientName, setValue]);

  // Prefill pharmacist from MiniCalendar filters, else default new appointments to "Any Pharmacist" when available
  React.useEffect(() => {
    if (!open) return;
    const currentVal = (watch("pharmacist") || "").trim();
    if (currentVal) return;
    const targetName = String(initialPharmacistName).trim().toLowerCase();
    const match = (servicePharmacists || []).find(
      (p: any) =>
        String(p?.name || "")
          .trim()
          .toLowerCase() === targetName ||
        String(p?.id || "")
          .trim()
          .toLowerCase() === targetName,
    );
    if (match && match.id) {
      setValue("pharmacist", String(match.id), {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [open, initialPharmacistName, servicePharmacists, setValue, watch]);

  const serviceType = watch("serviceType");
  const selectedDate = watch("date") || "";
  const selectedTime = watch("time") || "";
  const selectedPharmacist = watch("pharmacist") || "";
  const selectedPharmacistProfile = React.useMemo(
    () =>
      (servicePharmacists || []).find(
        (pharmacist: any) => String(pharmacist?.id) === selectedPharmacist,
      ),
    [selectedPharmacist, servicePharmacists],
  );
  const selectedPharmacistName =
    selectedPharmacistProfile?.name || "This pharmacist";
  const pharmacistAlreadyBooked = Boolean(
    selectedPharmacistProfile?.isAlreadyBooked,
  );
  const pharmacistUnavailable = Boolean(
    selectedPharmacistProfile?.isOutsideWorkingHours,
  );
  // Booked flags are per requested slot, so ignore them while they lag the selected time.
  const isSelectedSlotBookedForPharmacist =
    !isEditMode &&
    pharmacistAlreadyBooked &&
    (!activePharmacistsRequest ||
      activePharmacistsRequest.startTime === selectedTime);
  const selectedServiceInfo = React.useMemo(
    () =>
      (mappedServices || []).find(
        (service: any) => service?._id === serviceType,
      ),
    [mappedServices, serviceType],
  );

  const isServiceOutOfDateRange = React.useMemo(() => {
    if (!selectedDate) return false;
    const startDateOnly = selectedServiceInfo?.serviceStartDate
      ? String(selectedServiceInfo.serviceStartDate).split("T")[0]
      : null;
    const endDateOnly = selectedServiceInfo?.serviceEndDate
      ? String(selectedServiceInfo.serviceEndDate).split("T")[0]
      : null;
    if (startDateOnly && selectedDate < startDateOnly) return true;
    if (endDateOnly && selectedDate > endDateOnly) return true;
    return false;
  }, [selectedDate, selectedServiceInfo]);
  const serviceUnavailableOnDay = Boolean(
    selectedDate &&
    (selectedServiceInfo?.serviceUnavailableOnDay || isServiceOutOfDateRange),
  );

  const lastFetchedServiceDateRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!open || !companyId || !activePharmacyId) return;
    const targetDate =
      (isEditMode && (currentBooking as any)?.date) ||
      selectedDate ||
      initialDate ||
      new Date().toISOString().split("T")[0];
    if (lastFetchedServiceDateRef.current === targetDate) return;
    lastFetchedServiceDateRef.current = targetDate;
    getDefaultServicesByCompanyIdAndPharmacyId({
      companyId,
      pharmacyId: activePharmacyId,
      appointmentDate: targetDate,
    });
  }, [
    open,
    companyId,
    activePharmacyId,
    isEditMode,
    currentBooking,
    selectedDate,
    initialDate,
  ]);

  const noPharmacistAvailable = Boolean(
    selectedPharmacist === "any" &&
    servicePharmacists.length > 0 &&
    servicePharmacists.every(
      (pharmacist: any) =>
        pharmacist?.isAlreadyBooked || pharmacist?.isOutsideWorkingHours,
    ),
  );
  const [dateError, setDateError] = React.useState<string | null>(null);
  const [availabilityError, setAvailabilityError] = React.useState<
    string | null
  >(null);

  const serviceOptionsForSelect = React.useMemo(() => {
    const base = (mappedServices || []).filter(
      (service: any) =>
        service?.serviceType !== 1 && service?.serviceType !== 3,
    );

    const currentServiceId = String(
      serviceType || (currentBooking as any)?.serviceId || "",
    ).trim();

    if (!currentServiceId) return base;
    if (
      base.some((service: any) => String(service?._id) === currentServiceId)
    ) {
      return base;
    }

    const selectedFromAllRaw = [].find(
      (service: any) => String(service?._id) === currentServiceId,
    );

    const selectedFromAll = selectedFromAllRaw
      ? {
          _id: selectedFromAllRaw._id,
          name: selectedFromAllRaw.serviceName,
          serviceType: selectedFromAllRaw.serviceType,
          serviceDuration: selectedFromAllRaw.serviceDuration,
          isRoomRequired: selectedFromAllRaw.isRoomRequired,
        }
      : undefined;

    const selectedFromBooking = !selectedFromAll
      ? {
          _id: currentServiceId,
          name: String(
            (currentBooking as any)?.serviceName || "Selected Service",
          ).trim(),
          serviceType: (currentBooking as any)?.serviceType,
          serviceDuration: Number((currentBooking as any)?.duration || 0),
          isRoomRequired: false,
        }
      : undefined;

    if (selectedFromAll) return [selectedFromAll, ...base];
    if (selectedFromBooking) return [selectedFromBooking, ...base];
    return base;
  }, [mappedServices, serviceType, currentBooking]);

  // First priority: the pharmacy itself has no slots on the chosen date.
  const pharmacyUnavailableOnDate = Boolean(
    !isEditMode &&
      serviceType &&
      selectedDate &&
      !isUpdatePharmacyLocationLoading &&
      pharmacyAvailableSlotsDate === selectedDate &&
      pharmacyAvailableSlotsCount === 0,
  );

  React.useEffect(() => {
    if (pharmacyUnavailableOnDate && getValues("time")) {
      setValue("time", "", { shouldValidate: false, shouldDirty: true });
    }
  }, [pharmacyUnavailableOnDate, getValues, setValue]);

  // A time is unusable when every pharmacist is booked at it, per either the
  // get-active-pharmacists flags (remembered in fullyBookedTimes) or the
  // appointments already loaded on the calendar.
  const isTimeFullyBooked = React.useCallback(
    (time: string) =>
      isSlotFullyBookedFor(selectedDate, time, watch("duration")),
    [isSlotFullyBookedFor, selectedDate, serviceType, watch("duration")],
  );

  const availableTimes = React.useMemo(() => {
    let times: string[] = getAvailableTimesForDate
      ? getAvailableTimesForDate(selectedDate)
      : [];

    if (!times.length && getAggregatedTimesForDate) {
      // If no pharmacist selected, show slots based on service duration across all users
      const durationVal = parseInt(watch("duration") || "30", 10);
      times = getAggregatedTimesForDate(selectedDate, durationVal);
    }

    if (currentBooking?.time && !times.includes(currentBooking.time)) {
      times.push(currentBooking.time);
    }

    // Keep a calendar-slot-prefilled time selectable so the Select can display it.
    if (selectedTime && !times.includes(selectedTime)) {
      times.push(selectedTime);
    }

    return times.filter((t) => !isTimeFullyBooked(t)).sort();
  }, [
    getAvailableTimesForDate,
    getAggregatedTimesForDate,
    selectedDate,
    currentBooking?.time,
    selectedPharmacist,
    selectedTime,
    isTimeFullyBooked,
    watch("duration"),
  ]);

  const durationValueForSplit =
    parseInt(watch("duration") || "0", 10) ||
    Number(selectedServiceInfo?.serviceDuration || 0) ||
    30;

  const pharmacistWeekSlots = React.useMemo(() => {
    if (
      !selectedDate ||
      !durationValueForSplit ||
      !getPharmacistWeekAvailabilitySlots
    )
      return null;
    return getPharmacistWeekAvailabilitySlots(
      selectedDate,
      selectedPharmacist,
      durationValueForSplit,
    );
  }, [
    getPharmacistWeekAvailabilitySlots,
    selectedDate,
    selectedPharmacist,
    durationValueForSplit,
  ]);

  const roomDaySlots = React.useMemo(() => {
    if (
      !selectedDate ||
      !serviceType ||
      !selectedServiceInfo?.isRoomRequired ||
      !getServiceRoomSlotsForDate
    )
      return null;
    return getServiceRoomSlotsForDate(selectedDate, serviceType);
  }, [
    getServiceRoomSlotsForDate,
    selectedDate,
    serviceType,
    selectedServiceInfo?.isRoomRequired,
  ]);

  const isTimeWithinWindows = React.useCallback(
    (
      time: string,
      durationMinutes: number,
      windows: { startTime: string; endTime: string }[],
    ) => {
      const parseMins = (t: string) => {
        const [h, m] = String(t || "00:00").split(":");
        return parseInt(h || "0", 10) * 60 + parseInt(m || "0", 10);
      };
      const start = parseMins(time);
      const end = start + (durationMinutes || 0);
      return windows.some((w) => {
        const st = parseMins(w.startTime);
        const et = parseMins(w.endTime);
        return start >= st && end <= et;
      });
    },
    [],
  );

  const getSlotReasons = React.useCallback(
    (time: string, durationMinutes: number) => {
      const reasons: string[] = [];
      if (pharmacistWeekSlots !== null) {
        if (!pharmacistWeekSlots.includes(time))
          reasons.push("Outside pharmacist hours");
      }
      // Server flag covers the prefilled slot when week slots are unknown or mismatched.
      if (
        !reasons.length &&
        pharmacistUnavailable &&
        time === selectedTime
      ) {
        reasons.push("Outside pharmacist hours");
      }
      if (roomDaySlots !== null) {
        const within =
          roomDaySlots.length &&
          isTimeWithinWindows(time, durationMinutes, roomDaySlots);
        if (!within) reasons.push("Room unavailable");
      }
      return reasons;
    },
    [
      pharmacistWeekSlots,
      roomDaySlots,
      isTimeWithinWindows,
      pharmacistUnavailable,
      selectedTime,
    ],
  );

  const timesWithReasons = React.useMemo(
    () =>
      availableTimes.map((t) => ({
        time: t,
        reasons: getSlotReasons(t, durationValueForSplit),
      })),
    [availableTimes, getSlotReasons, durationValueForSplit],
  );

  const bookableTimes = React.useMemo(() => {
    const base =
      pharmacistWeekSlots !== null ? pharmacistWeekSlots : availableTimes;
    let list =
      roomDaySlots === null
        ? base
        : base.filter(
            (t) =>
              roomDaySlots.length &&
              isTimeWithinWindows(t, durationValueForSplit, roomDaySlots),
          );

    const existingTime = currentBooking?.time || selectedTime || "";
    if (existingTime && !list.includes(existingTime)) {
      list = [...list, existingTime].sort();
    }
    return list.filter((t) => !isTimeFullyBooked(t));
  }, [
    pharmacistWeekSlots,
    availableTimes,
    roomDaySlots,
    isTimeWithinWindows,
    durationValueForSplit,
    currentBooking?.time,
    selectedTime,
    isTimeFullyBooked,
  ]);
  const adHocTimes = React.useMemo(
    () => timesWithReasons.filter((t) => t.reasons.length > 0),
    [timesWithReasons],
  );

  const selectableTimes = React.useMemo(
    () => (showAllServiceTimes ? availableTimes : bookableTimes),
    [showAllServiceTimes, availableTimes, bookableTimes],
  );

  // Runs once per open so the user can still switch the toggle off afterwards.
  const autoShowAllTimesDoneRef = React.useRef(false);
  React.useEffect(() => {
    if (!open) {
      autoShowAllTimesDoneRef.current = false;
      return;
    }
    if (autoShowAllTimesDoneRef.current || !selectedTime) return;
    if (initialTime && selectedTime !== initialTime) return;
    if (!adHocTimes.some((t) => t.time === selectedTime)) return;
    autoShowAllTimesDoneRef.current = true;
    if (isEditMode) {
      // An existing ad-hoc appointment must keep its time visible.
      setShowAllServiceTimes(true);
      return;
    }
    suppressTimeAutoFillRef.current = true;
    setBookedSlotMessage(
      `${selectedPharmacistProfile?.name || "The pharmacist"} is not available at ${selectedTime}. Please choose another time, or turn on "Show all service times" to book it as ad-hoc.`,
    );
    setValue("time", "", { shouldValidate: false, shouldDirty: true });
  }, [
    open,
    selectedTime,
    initialTime,
    adHocTimes,
    isEditMode,
    selectedPharmacistProfile,
    setValue,
  ]);

  // Picking a pharmacist who doesn't work at the chosen time clears it instead of keeping an ad-hoc time.
  const prevSelectedPharmacistRef = React.useRef(selectedPharmacist);
  React.useEffect(() => {
    if (prevSelectedPharmacistRef.current === selectedPharmacist) return;
    prevSelectedPharmacistRef.current = selectedPharmacist;
    if (!open || isEditMode || showAllServiceTimes) return;
    if (!selectedPharmacist || selectedPharmacist === "any") return;
    if (!selectedTime || !adHocTimes.some((t) => t.time === selectedTime))
      return;
    suppressTimeAutoFillRef.current = true;
    setBookedSlotMessage(
      `${selectedPharmacistProfile?.name || "The pharmacist"} is not available at ${selectedTime}. Please choose another time, or turn on "Show all service times" to book it as ad-hoc.`,
    );
    setValue("time", "", { shouldValidate: false, shouldDirty: true });
  }, [
    open,
    isEditMode,
    showAllServiceTimes,
    selectedPharmacist,
    selectedTime,
    adHocTimes,
    selectedPharmacistProfile,
    setValue,
  ]);

  const selectFirstAvailableSlot = React.useCallback(
    (response: any) => {
      const slots = Array.isArray(response?.pharmacyAvailableSlots)
        ? response.pharmacyAvailableSlots
            .map((slot: any) => String(slot || "").slice(0, 5))
            .filter(
              (slot: string) =>
                !isSlotFullyBookedFor(
                  getValues("date"),
                  slot,
                  getValues("duration"),
                ),
            )
        : [];
      if (!slots.length) return;

      const currentTime = getValues("time");
      if (suppressTimeAutoFillRef.current && !currentTime) return;
      if (currentTime && slots.includes(currentTime)) return;
      // Keep the time the user picked by clicking a calendar slot.
      if (currentTime && initialTime && currentTime === initialTime) return;

      // Prefer the first slot the Time dropdown will list (pharmacist hours),
      // not just the pharmacy's earliest slot.
      const grid = showAllServiceTimes
        ? null
        : getPharmacistWeekAvailabilitySlots?.(
            getValues("date"),
            getValues("pharmacist"),
            Number(getValues("duration")) || 0,
          );
      const preferredSlot =
        (grid && slots.find((s: string) => grid.includes(s))) || slots[0];

      setValue("time", preferredSlot, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [
      setValue,
      getValues,
      initialTime,
      isSlotFullyBookedFor,
      showAllServiceTimes,
      getPharmacistWeekAvailabilitySlots,
    ],
  );

  const disabledDate = React.useCallback(
    (d: Date) => {
      const key = formatDateFromDate
        ? formatDateFromDate(d)
        : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
            2,
            "0",
          )}-${String(d.getDate()).padStart(2, "0")}`;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const d0 = new Date(d);
      d0.setHours(0, 0, 0, 0);
      return d0 < today || !availableDates?.includes(key);
    },
    [availableDates, formatDateFromDate],
  );

  // Clear date/time if not compatible with availability
  React.useEffect(() => {
    if (isEditMode) return;
    if (slotSelectedDate && selectedDate === slotSelectedDate) {
      return;
    }
    if (
      pickedDateInFlightRef.current === selectedDate ||
      pharmacyUnavailableOnDate
    ) {
      return;
    }
    if (
      selectedDate &&
      Array.isArray(availableDates) &&
      availableDates.length > 0 &&
      !availableDates.includes(selectedDate)
    ) {
      setValue("date", "");
      setValue("time", "");
    }
  }, [
    availableDates,
    isEditMode,
    selectedDate,
    setValue,
    slotSelectedDate,
    pharmacyUnavailableOnDate,
  ]);

  // Show validation error when pharmacist has no availability
  React.useEffect(() => {
    if (currentBooking) {
      setAvailabilityError(null);
      return;
    }
    if (!selectedPharmacist || !serviceType) {
      setAvailabilityError(null);
      return;
    }
    if (selectedPharmacist === "any") {
      setAvailabilityError(null);
      return;
    }
    const isNewlyCreated = isEditMode && currentBooking;
    if (isNewlyCreated) {
      setAvailabilityError(null);
      return;
    }
    if (Array.isArray(availableDates) && availableDates.length === 0) {
      // Pharmacy-level unavailability (shown as its own message) takes priority.
      const currentDate = getValues("date");
      if (
        currentDate &&
        (pickedDateInFlightRef.current === currentDate ||
          pharmacyUnavailableOnDate)
      ) {
        setAvailabilityError(null);
        return;
      }
      setAvailabilityError("This user has no available dates or time slots.");
      // A date/time chosen via a calendar slot click must survive this check.
      if (!(slotSelectedDate && getValues("date") === slotSelectedDate)) {
        setValue("date", "");
        setValue("time", "");
      }
    } else {
      setAvailabilityError(null);
    }
  }, [
    availableDates,
    selectedPharmacist,
    serviceType,
    isEditMode,
    currentBooking,
    slotSelectedDate,
    pharmacyUnavailableOnDate,
  ]);

  React.useEffect(() => {
    if (currentBooking) return;
    // Without a service selected yet, selectableTimes is always empty (no
    // availableTimes/bookableTimes data has been fetched), so there's no
    // real basis to judge the prefilled slot time as invalid - skip until a
    // service is chosen, otherwise this immediately wipes the time a
    // calendar slot click just prefilled, before onServiceTypeSelected even
    // gets a chance to run with it.
    if (!serviceType) return;
    if (pharmacyUnavailableOnDate) return;
    // Likewise, don't "correct" the time against an empty list before the
    // slots API has actually resolved for this service/date - that's not a
    // real signal the prefilled time is invalid, just that data hasn't
    // arrived yet.
    if (selectableTimes.length === 0) return;
    if (suppressTimeAutoFillRef.current && !selectedTime) return;
    if (selectedTime && selectableTimes.includes(selectedTime)) return;
    const fallback = selectableTimes[0] || "";
    if (selectedTime !== fallback) {
      setValue("time", fallback, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [
    selectableTimes,
    selectedTime,
    currentBooking,
    serviceType,
    pharmacyUnavailableOnDate,
  ]);

  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const onSubmit = React.useCallback(
    async (data: BookingForm) => {
      setSubmitError(null);
      setIsSubmitting(true);

      try {
        if (
          !showAllServiceTimes &&
          adHocTimes.some((t) => t.time === data.time)
        ) {
          setSubmitError(
            'This time is outside normal availability. Enable "Show all service times" to book it as an ad-hoc appointment.',
          );
          return;
        }

        const taskStrings = localTasks.map((t) => t.label);
        const payload: any = {
          ...data,
          appointmentType: pharmacistUnavailable ? 4 : 1,
          otherPharmacyId: activePharmacyId,
          ...(taskStrings.length ? { tasksToComplete: taskStrings } : {}),
          ...(localVaccines.length
            ? { vaccinesAdminister: localVaccines }
            : {}),
        };

        if (
          !validateTemplateAvailability(
            data,
            confirmCancelTemplates,
            setValue,
            setError,
            isEditMode ? "update" : "insert",
          )
        ) {
          return;
        }

        // const validationResult = validatePharmacySlots(data);
        // if (!validationResult.isValid) {
        //   setSubmitError(validationResult.message);
        //   return;
        // }
        const validationResult = validateBookingConflicts(
          data,
          currentBooking?.id,
        );
        if (!validationResult.isValid) {
          setSubmitError(validationResult.message);
          return;
        }
        await Promise.resolve(onSave(payload));

        if (bookingSlotsMode === "fromAppointment") {
          const patientIdForRefresh = String(
            data.patientId ||
              selectedPatientId ||
              (currentBooking as any)?.patientId ||
              (currentBooking as any)?.patientID ||
              "",
          ).trim();

          if (patientIdForRefresh) {
            if (!isConsultationRoute) {
              await getPatientAppointmentsByPatientId(patientIdForRefresh);
            }
          }
        }

        if (currentUser && currentUser.emailAddress) {
          trackFirstTimeIntercomEvent({
            eventName: "calendar_appointment_first_time",
            user: {
              id: currentUser.emailAddress,
              email: currentUser.emailAddress,
              name: currentUser.fullName || "",
            },
            properties: {
              source: "marketplace_settings",
            },
          });
        }
        // Close only on success
        setOpen(false);
        if (!isEditMode) {
          reset({
            duration: "15",
            date: "",
            time: "",
            patientName: "",
            patientId: "",
            pharmacist: "",
            serviceType: "",
            room: "",
            notes: "",
            email: "",
            mobile: "",
            confirmationMethod: undefined,
          });
          setPatientSearchQuery("");
          setLocalTasks([]);
          setLocalVaccines([]);
          setInternalTaskLabel("");
          setInternalVaccineId("");
          lastResetKeyRef.current = null;
        }
      } catch (e) {
        const message =
          (e && (e.message || e.toString())) ||
          "Unable to create appointment. Please review the fields.";
        setSubmitError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      onSave,
      isEditMode,
      reset,
      setOpen,
      currentUser,
      localTasks,
      localVaccines,
      pharmacistUnavailable,
      showAllServiceTimes,
      adHocTimes,
    ],
  );

  const watchedDate = watch("date");
  const watchedTime = watch("time");
  const watchedDuration = watch("duration");
  const watchedPharmacist = watch("pharmacist");
  const watchedRoom = watch("room");

  React.useEffect(() => {
    if (submitError) setSubmitError(null);
  }, [
    watchedDate,
    watchedTime,
    watchedDuration,
    watchedPharmacist,
    watchedRoom,
  ]);

  // Clear inline error when modal closes
  React.useEffect(() => {
    if (!open) {
      setSubmitError(null);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      setAvailabilityError(null);
      setDateError(null);
      setValue("date", "");
      setValue("time", "");
      setValue("patientName", "");
      setValue("patientId", "");
      setValue("email", "");
      setValue("mobile", "");
      setValue("notes", "");
      setValue("room", "");
      setPatientSearchQuery("");
      setFilteredPatients([]);
      // Reset partial match state when modal closes
      setLocalPartialMatch(false);
      setLocalMatchingPatients([]);
      // Clear all task and vaccine state when modal closes
      setLocalTasks([]);
      setLocalVaccines([]);
      setInternalTaskLabel("");
      setInternalVaccineId("");
      lastResetKeyRef.current = null;
      // Force the services list to be re-fetched for whatever date this
      // modal is opened with next time, rather than skipping the fetch
      // because it matches a stale date from this closed session.
      lastFetchedServiceDateRef.current = null;
    }
  }, [open, setValue]);

  React.useEffect(() => {}, [
    localPartialMatch,
    showPatientResults,
    showQuickAddPatient,
    patientSearchQuery,
    localMatchingPatients,
  ]);

  const currentDuration = watch("duration");
  const previousDurationServiceTypeRef = React.useRef("");
  const durations = React.useMemo(() => {
    const list = [...(serviceDurations || [])];
    const selectedSvc = findServiceById(serviceType);
    if (list.length === 0 && selectedSvc) {
      const base =
        parseInt(String((selectedSvc as any)?.serviceDuration ?? 0), 10) || 0;
      if (base > 0) {
        const slots = Array.from({ length: 5 }, (_, i) => base * (i + 1));
        return slots.map((m) => ({
          value: String(m),
          label: `${m} minutes`,
        }));
      }
    }
    if (
      currentBooking?.duration &&
      !list.some((d) => d.value === String(currentBooking.duration))
    ) {
      list.push({
        value: String(currentBooking.duration),
        label: `${currentBooking.duration} minutes`,
      });
    }
    if (
      currentDuration &&
      !list.some((d) => d.value === String(currentDuration))
    ) {
      list.push({
        value: String(currentDuration),
        label: `${currentDuration} minutes`,
      });
    }

    return list;
  }, [
    currentDuration,
    currentBooking,
    serviceDurations,
    serviceType,
    findServiceById,
  ]);

  React.useEffect(() => {
    if (!serviceType) {
      previousDurationServiceTypeRef.current = "";
      return;
    }

    const previousServiceType = previousDurationServiceTypeRef.current;
    const serviceTypeChanged = previousServiceType !== serviceType;
    previousDurationServiceTypeRef.current = serviceType;

    if (serviceTypeChanged) {
      const bookingServiceType = String(
        (currentBooking as any)?.serviceId ||
          (currentBooking as any)?.serviceType ||
          "",
      );
      const isInitialEditService =
        !previousServiceType &&
        currentBooking &&
        bookingServiceType === serviceType;

      if (!isInitialEditService) {
        const selectedService = findServiceById(serviceType);
        const initialDuration = getInitialDurationForService(selectedService);

        if (currentDuration !== initialDuration) {
          setValue("duration", initialDuration, {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
        return;
      }
    }

    if (!durations.length) return;
    if (currentDuration && durations.some((d) => d.value === currentDuration)) {
      return;
    }

    setValue("duration", durations[0].value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [
    currentBooking,
    currentDuration,
    durations,
    getInitialDurationForService,
    findServiceById,
    serviceType,
    setValue,
  ]);

  const todayStr = React.useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  const handleBackToSlots = React.useCallback(() => {
    setOpen(false);
    setShowBookingSlots(true);
    setIsConsultationRoute(false);
  }, []);

  const bookingData = currentBooking as any;

  const hasPatientId = !!bookingData?.patientId;

  const isStartConsultationDisabled =
    isEventCalendarLoading ||
    isPatientDetailsLoading ||
    [1, 4].includes(bookingData?.eventStatus) ||
    isConsultationCompleted;
  const timeSelectLoading = isUpdatePharmacyLocationLoading;
  const serviceTypeSelectLoading =
    isPharmacyServiceLoading && (mappedServices || []).length === 0;
  const pharmacistSelectLoading =
    isPharmacistLoading || isServiceUsersByCompanyLoading;
  const roomSelectLoading = isRoomsLoading || isAppointmentRoomsLoading;
  // Date depends on the service/pharmacist being resolved first, so show the
  // same loading treatment while either of those is still settling.
  const dateSelectLoading = serviceTypeSelectLoading || pharmacistSelectLoading;

  // When every pharmacist is already booked for the chosen slot, the slot is
  // unusable: hide it from the time lists, clear the fields and tell the user.
  React.useEffect(() => {
    if (!open || isEditMode || pharmacistSelectLoading) return;
    if (pharmacyUnavailableOnDate) return;
    if (!serviceType || !selectedDate || !selectedTime) return;
    const req = activePharmacistsRequest;
    const duration = watch("duration");
    // API flags only apply if they were computed for this exact slot.
    const apiAllBooked =
      !!req &&
      req.serviceId === serviceType &&
      req.date === selectedDate &&
      req.startTime === selectedTime &&
      req.duration === Number(duration) &&
      servicePharmacists.length > 0 &&
      servicePharmacists.every((p: any) => p?.isAlreadyBooked);
    // Calendar appointments cover slot clicks where the API flags don't line up.
    const calendarAllBooked = isTimeFullyBooked(selectedTime);
    if (!apiAllBooked && !calendarAllBooked) return;

    const key = `${serviceType}|${selectedDate}|${duration}|${selectedTime}`;
    suppressTimeAutoFillRef.current = true;
    setFullyBookedTimes((prev) => (prev.includes(key) ? prev : [...prev, key]));
    setBookedSlotMessage(
      `The ${selectedTime} slot is not available for the pharmacist as it is already booked. Please choose another time.`,
    );
    setValue("time", "", { shouldValidate: false, shouldDirty: true });
    setValue("pharmacist", "", { shouldValidate: false, shouldDirty: true });
  }, [
    open,
    isEditMode,
    pharmacistSelectLoading,
    serviceType,
    selectedDate,
    selectedTime,
    activePharmacistsRequest,
    servicePharmacists,
    isTimeFullyBooked,
    pharmacyUnavailableOnDate,
    watch("duration"),
  ]);

  // Whatever time ends up in the form (auto-picked or corrected later) must be
  // the startTime get-active-pharmacists was called with.
  const startTimeSyncKeyRef = React.useRef<string>("");
  React.useEffect(() => {
    if (!open || isEditMode || pharmacistSelectLoading) return;
    if (pharmacyUnavailableOnDate) return;
    if (!serviceType || !selectedDate || !selectedTime) return;
    const req = activePharmacistsRequest;
    const duration = watch("duration");
    if (
      !req ||
      req.serviceId !== serviceType ||
      req.date !== selectedDate ||
      req.duration !== Number(duration) ||
      req.startTime === selectedTime
    )
      return;
    const key = `${serviceType}|${selectedDate}|${duration}|${selectedTime}|${req.startTime}`;
    if (startTimeSyncKeyRef.current === key) return;
    startTimeSyncKeyRef.current = key;
    onServiceTypeSelected?.({
      ...getBookingFormData(),
      skipAvailableSlots: true,
    });
  }, [
    open,
    isEditMode,
    pharmacistSelectLoading,
    pharmacyUnavailableOnDate,
    serviceType,
    selectedDate,
    selectedTime,
    activePharmacistsRequest,
    watch("duration"),
  ]);

  const handleCancel = () => {
    setOpen(false);
    setIsConsultationRoute(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent
          onEscapeKeyDown={(e) => {
            if (showPatientResults) {
              e.preventDefault();
              setShowPatientResults(false);
              return;
            }
            if (showDeleteConfirm) {
              e.preventDefault();
              setShowDeleteConfirm(false);
              return;
            }
            if (showMatchDialog) {
              e.preventDefault();
              setShowMatchDialog(false);
              return;
            }
            setTimeout(() => {
              document.body.style.pointerEvents = "";
              if (document.body.style.overflow === "hidden") {
                document.body.style.overflow = "";
              }
            }, 0);
          }}
          onPointerDownOutside={() => {
            setTimeout(() => {
              document.body.style.pointerEvents = "";
              if (document.body.style.overflow === "hidden") {
                document.body.style.overflow = "";
              }
            }, 0);
          }}
          className={`sm:max-w-[600px] transition-colors duration-300 max-h-[100vh] overflow-y-auto ${
            isDarkMode
              ? "bg-gray-800 border-gray-600 text-white"
              : "bg-white border-gray-200 text-gray-900"
          }`}
        >
          <DialogHeader className={`flex-shrink-0`}>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {showBacktoSlotsButton && !showBookingSlots && !isEditMode && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToSlots}
                    className={`p-2 ${
                      isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                    }`}
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                )}
                <DialogTitle
                  className={`text-lg font-semibold transition-colors duration-300 ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {isEditMode
                    ? "Edit Appointment"
                    : showBookingSlots
                      ? "Select Time Slot"
                      : "New Appointment"}
                </DialogTitle>

                {isEditMode &&
                  (currentBooking?.appointmentType === 1 ||
                    currentBooking?.appointmentType === 2) && (
                    <Badge
                      variant="outline"
                      className={
                        currentBooking?.appointmentType === 2
                          ? "border-blue-600 text-blue-700 bg-blue-50 gap-1"
                          : "border-gray-400 text-gray-600 bg-gray-50 gap-1"
                      }
                      title={
                        currentBooking?.appointmentType === 2
                          ? "This appointment was booked online"
                          : "This appointment was booked in-store"
                      }
                    >
                      {currentBooking?.appointmentType === 2 ? (
                        <Globe className="w-3 h-3" />
                      ) : (
                        <Store className="w-3 h-3" />
                      )}
                      {currentBooking?.appointmentType === 2
                        ? "Booked online"
                        : "Booked in-store"}
                    </Badge>
                  )}
              </div>
            </div>
          </DialogHeader>

          {/* {availabilityError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="w-4 h-4" />
            <AlertTitle>Not available</AlertTitle>
            <AlertDescription>
              This pharmacist has no available dates or time slots. Please
              choose a different pharmacist or service.
            </AlertDescription>
          </Alert>
        )} */}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <Accordion
              type="multiple"
              defaultValue={["essential-info", "notifications"]}
              className={`w-full border-t transition-colors duration-300 ${
                isDarkMode ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <AccordionItem
                value="essential-info"
                className={`border-b transition-colors duration-300 ${
                  isDarkMode ? "border-gray-600" : "border-gray-200"
                }`}
              >
                <AccordionTrigger
                  className={`px-0 py-4 ${
                    isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2 font-semibold">
                    <Calendar className="w-5 h-5" />
                    <span>Essential Information</span>
                    {isCancelledAppointment && (
                      <span
                        className={`px-2 py-0.5 text-xs font-semibold rounded ${
                          isDarkMode
                            ? "bg-red-900/40 text-red-200 border border-red-700"
                            : "bg-red-100 text-red-700 border border-red-200"
                        }`}
                      >
                        (Cancelled)
                      </span>
                    )}
                    {/* {selectedPatient && (
              <Badge className="ml-2" variant="secondary">
                {selectedPatient.name}
              </Badge>
            )} */}
                  </div>
                </AccordionTrigger>
                {showQuickAddPatient ? (
                  <InlineQuickAddPatient
                    isDarkMode={isDarkMode}
                    defaultValues={quickAddDefaults}
                    onCancel={() => {
                      setShowQuickAddPatient(false);
                      // Clear search query when canceling
                      setPatientSearchQuery("");
                    }}
                    onSave={async (formData) => {
                      // Call handleSavePatient with the form data
                      await handleSavePatient(formData);
                      // The partial match state will be set inside handleSavePatient
                    }}
                  />
                ) : (
                  <AccordionContent className="pt-0 px-2 pb-6 !overflow-visible">
                    <div className="grid grid-cols-2 gap-4">
                      {/* Patient Name (full width) */}
                      <div className="col-span-2 space-y-2">
                        <Label
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-700"
                          }
              `}
                        >
                          Patient *
                        </Label>
                        <div className="relative w-full">
                          {/* Search Icon */}
                          <Search
                            className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors duration-300 ${
                              isDarkMode ? "text-gray-400" : "text-gray-400"
                            } pointer-events-none`}
                          />

                          {/* Input */}
                          <Input
                            ref={patientInputRef as any}
                            placeholder="Search Patient"
                            value={patientSearchQuery}
                            onChange={(e) =>
                              handlePatientSearch(e.target.value)
                            }
                            onFocus={() => {
                              if (
                                patientSearchQuery.length > 0 &&
                                sortedPatients?.length > 0
                              ) {
                                setShowPatientResults(true);
                              }
                            }}
                            onBlur={() => {
                              setTimeout(
                                () => setShowPatientResults(false),
                                200,
                              );
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Tab") {
                                setShowPatientResults(false);
                              } else if (e.key === "Escape") {
                                setShowPatientResults(false);
                                patientInputRef.current?.blur();
                              }
                            }}
                            disabled={
                              isEventCalendarLoading ||
                              isEditMode ||
                              bookingSlotsMode === "fromAppointment"
                            }
                            maxLength={200}
                            className={`pl-10 transition-colors duration-300 ${
                              isDarkMode
                                ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                                : "bg-white border-gray-200"
                            }`}
                          />

                          {/* Patient Results Dropdown */}
                          {showPatientResults && sortedPatients?.length > 0 && (
                            <div
                              className={`absolute top-full left-0 right-0 mt-1 border rounded-lg text-sm shadow-lg z-50 max-h-80 overflow-y-auto transition-colors duration-300 ${
                                isDarkMode
                                  ? "bg-gray-800 border-gray-600"
                                  : "bg-white border-gray-200"
                              }`}
                            >
                              {sortedPatients.map((patient: any) => (
                                <div
                                  key={patient.id || patient.patientID}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    handlePatientSelect(patient);
                                  }}
                                  onClick={() => {
                                    handlePatientSelect(patient);
                                  }}
                                  className={`p-3 border-b last:border-b-0 hover:transition-colors duration-200 cursor-pointer ${
                                    isDarkMode
                                      ? "border-gray-700 hover:bg-gray-700"
                                      : "border-gray-100 hover:bg-gray-50"
                                  }`}
                                >
                                  <div
                                    className={`text-sm font-medium transition-colors duration-300 ${
                                      isDarkMode
                                        ? "text-white"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    {patient.patientName}
                                  </div>
                                  {(patient.nhsNumber || patient.dob) && (
                                    <div
                                      className={`text-xs transition-colors duration-300 ${
                                        isDarkMode
                                          ? "text-gray-400"
                                          : "text-gray-500"
                                      }`}
                                    >
                                      NHS: {patient.nhsNumber} • DOB:{" "}
                                      {getFormattedDate(patient.dob)}
                                    </div>
                                  )}
                                  {patient.address && (
                                    <div
                                      className={`text-xs transition-colors duration-300 ${
                                        isDarkMode
                                          ? "text-gray-500"
                                          : "text-gray-400"
                                      }`}
                                    >
                                      {patient.address}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* No Results */}
                          {showPatientResults &&
                            patientSearchQuery.length > 0 &&
                            filteredPatients?.length === 0 && (
                              <div
                                className={`absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-50 p-4 text-center transition-colors duration-300 ${
                                  isDarkMode
                                    ? "bg-gray-800 border-gray-600 text-gray-400"
                                    : "bg-white border-gray-200 text-gray-500"
                                }`}
                              >
                                <Search className="w-6 h-6 mx-auto mb-2 opacity-50" />

                                <p className="text-sm font-medium">
                                  No patients found
                                </p>

                                <p className="mt-1 text-xs opacity-75">
                                  Try searching by name or NHS number
                                </p>

                                <button
                                  type="button"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setShowPatientResults(false);
                                    handleAddPatient(patientSearchQuery);
                                  }}
                                  className="w-full px-3 py-2 mt-3 text-sm font-medium text-white transition-colors bg-blue-600 rounded-md hover:bg-blue-700"
                                >
                                  + Add New Patient
                                </button>
                              </div>
                            )}
                        </div>
                        {errors.patientName && (
                          <p
                            className={`text-sm transition-colors duration-300 ${
                              isDarkMode ? "text-red-400" : "text-red-600"
                            }
                `}
                          >
                            {errors.patientName.message}
                          </p>
                        )}
                      </div>

                      {/* Service Type */}
                      <div className="space-y-2">
                        <Label
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-700"
                          }
                          `}
                        >
                          Service Type *
                        </Label>
                        {serviceTypeSelectLoading ? (
                          <Skeleton className="w-full h-10 animate-pulse" />
                        ) : (
                          <Select
                            value={serviceType}
                            onValueChange={async (value: string) => {
                              pharmacistPrefillPending.current = false;
                              clearBookedSlotNotice();
                              try {
                                setValue("pharmacist", "");
                                setIsPharmacistLoading(true);
                                setBookingConfirmationLoading(true);
                                setValue("serviceType", value);
                                const svc = (mappedServices || []).find(
                                  (s: any) => s?._id === value,
                                );
                                setValue(
                                  "duration",
                                  getInitialDurationForService(svc),
                                  {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  },
                                );
                                // With a date chosen, refetch slots; the time falls back to the first
                                // available slot, which is then sent to get-active-pharmacists.
                                const hasDate = Boolean(getValues("date"));
                                const slotsResponse =
                                  await onServiceTypeSelected?.({
                                    ...getBookingFormData(),
                                    ...(hasDate
                                      ? { forceRefreshAvailability: true }
                                      : { skipAvailableSlots: true }),
                                  });
                                selectFirstAvailableSlot(slotsResponse);

                                // If pharmacist is already selected, we need to re-fetch availability
                                // because the service duration might have changed
                                const currentPharmacistId =
                                  getValues("pharmacist");
                                if (
                                  currentPharmacistId &&
                                  onPharmacistSelected
                                ) {
                                  await onPharmacistSelected(
                                    value,
                                    currentPharmacistId,
                                    activePharmacyId,
                                    false,
                                  );
                                }
                                try {
                                  setIsRoomsLoading(true);
                                  const formData = {
                                    ...getBookingFormData(),
                                    otherPharmacyId: activePharmacyId,
                                    PharmacyID: activePharmacyId,
                                    OtherPharmacyId: activePharmacyId,
                                  };
                                  await handleGetAppointmentRooms(formData);
                                } finally {
                                  setIsRoomsLoading(false);
                                }

                                const formData = getBookingFormData();
                                await handleGetAppointmentRooms(formData);
                              } finally {
                                setValue("serviceType", value);
                                setIsPharmacistLoading(false);
                                setBookingConfirmationLoading(false);
                                const svc = (mappedServices || []).find(
                                  (s: any) => s?._id === value,
                                );
                                const base =
                                  parseInt(
                                    String((svc as any)?.serviceDuration ?? 0),
                                    10,
                                  ) || 0;
                                if (base > 0)
                                  setValue("duration", String(base));
                              }
                            }}
                            disabled={isSubmitting || isConsultationCompleted}
                          >
                            <SelectTrigger
                              className="w-full"
                              disabled={isSubmitting || isConsultationCompleted}
                            >
                              <SelectValue placeholder="Select service" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem key="" value="">
                                Select Service
                              </SelectItem>
                              {mappedServices
                                ?.filter(
                                  (service) =>
                                    service?.serviceType !== 1 &&
                                    service?.serviceType !== 3,
                                )
                                .map((service) => (
                                  <SelectItem
                                    key={service?._id}
                                    value={service?._id}
                                  >
                                    {service?.name}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                        )}
                        {errors.serviceType && (
                          <p
                            className={`text-sm transition-colors duration-300 ${
                              isDarkMode ? "text-red-400" : "text-red-600"
                            }
                            `}
                          >
                            {errors.serviceType.message}
                          </p>
                        )}
                      </div>

                      {/* Date */}
                      <div className="space-y-2">
                        <Label
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-700"
                          }
                        `}
                        >
                          Date *
                        </Label>
                        {dateSelectLoading ? (
                          <Skeleton className="w-full h-10 animate-pulse" />
                        ) : (
                          <Popover
                            open={isDatePickerOpen}
                            onOpenChange={setIsDatePickerOpen}
                          >
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                id="date"
                                disabled={
                                  isSubmitting ||
                                  isEventCalendarLoading ||
                                  !serviceType ||
                                  isConsultationCompleted
                                }
                                className={`flex items-center w-full h-10 px-3 text-sm border rounded-md transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${
                                  isDarkMode
                                    ? "border-gray-600 bg-gray-700 text-white"
                                    : "border-gray-300 bg-white text-gray-900"
                                }`}
                              >
                                <Calendar className="w-4 h-4 mr-2 opacity-60" />
                                {selectedDate
                                  ? selectedDate.split("-").reverse().join("/")
                                  : "Select date"}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="start"
                            >
                              <DatePickerCalendar
                                mode="single"
                                // Parse as local calendar date (not UTC) to avoid
                                // off-by-one day shifts from new Date("YYYY-MM-DD").
                                selected={
                                  selectedDate
                                    ? (() => {
                                        const [y, m, d] = selectedDate
                                          .split("-")
                                          .map(Number);
                                        return new Date(
                                          y,
                                          (m || 1) - 1,
                                          d || 1,
                                        );
                                      })()
                                    : undefined
                                }
                                disabled={(date) => {
                                  const start = new Date();
                                  start.setHours(0, 0, 0, 0);
                                  return date < start;
                                }}
                                onSelect={async (date) => {
                                  if (!date) return;
                                  // Only fires on an explicit day click, so it's
                                  // safe to act on immediately - no debounce needed.
                                  const v = formatDateFromDate
                                    ? formatDateFromDate(date)
                                    : `${date.getFullYear()}-${String(
                                        date.getMonth() + 1,
                                      ).padStart(2, "0")}-${String(
                                        date.getDate(),
                                      ).padStart(2, "0")}`;
                                  setIsDatePickerOpen(false);
                                  clearBookedSlotNotice();
                                  pickedDateInFlightRef.current = v;
                                  setValue("date", v, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                  });

                                  try {
                                  const formData = getBookingFormData();
                                  const slotsResponse =
                                    await onServiceTypeSelected?.(formData);
                                  selectFirstAvailableSlot(slotsResponse);
                                  await handleGetAppointmentRooms(formData);

                                  setDateError(null);
                                  // Prefer the freshly awaited slots over the
                                  // getAvailableTimesForDate closure, which can be
                                  // stale (e.g. in edit mode, before this render's
                                  // service-users fetch has settled) and would wipe
                                  // out the slot selectFirstAvailableSlot just set.
                                  const freshSlots = Array.isArray(
                                    slotsResponse?.pharmacyAvailableSlots,
                                  )
                                    ? slotsResponse.pharmacyAvailableSlots.map(
                                        (slot: any) =>
                                          String(slot || "").slice(0, 5),
                                      )
                                    : null;
                                  const times =
                                    freshSlots ||
                                    (getAvailableTimesForDate
                                      ? getAvailableTimesForDate(v)
                                      : []);
                                  if (
                                    times.length > 0 &&
                                    !times.includes(watch("time") || "")
                                  ) {
                                    setValue("time", "");
                                  }
                                  } finally {
                                    pickedDateInFlightRef.current = null;
                                  }
                                }}
                              />
                            </PopoverContent>
                          </Popover>
                        )}
                        {errors.date && (
                          <p
                            className={`text-sm transition-colors duration-300 ${
                              isDarkMode ? "text-red-400" : "text-red-600"
                            }
                            `}
                          >
                            {errors.date.message}
                          </p>
                        )}
                        {dateError && (
                          <p
                            className={`text-sm mt-1 ${
                              isDarkMode ? "text-red-400" : "text-red-600"
                            }`}
                          >
                            {dateError}
                          </p>
                        )}
                      </div>

                      {/* Time */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <Label
                            className={`text-sm font-medium transition-colors duration-300 ${
                              isDarkMode ? "text-gray-200" : "text-gray-700"
                            }
                        `}
                          >
                            Time *
                          </Label>
                          <div className="flex items-center gap-1.5">
                            <Switch
                              id="show-all-times"
                              checked={showAllServiceTimes}
                              onCheckedChange={async (checked) => {
                                setShowAllServiceTimes(checked);
                                setBookedSlotMessage(null);
                                if (checked) return;
                                // bookableTimes always keeps the current time, so the
                                // auto-correct effect can't reset an ad-hoc pick itself.
                                const currentTime = getValues("time");
                                if (
                                  !currentTime ||
                                  !adHocTimes.some((t) => t.time === currentTime)
                                )
                                  return;
                                const firstBookable =
                                  bookableTimes.find(
                                    (t) =>
                                      !adHocTimes.some((a) => a.time === t),
                                  ) || "";
                                clearBookedSlotNotice();
                                setValue("time", firstBookable, {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                });
                                if (!firstBookable) return;
                                setIsRoomsLoading(true);
                                try {
                                  const formData = {
                                    ...getBookingFormData(),
                                    skipAvailableSlots: true,
                                  };
                                  await onServiceTypeSelected?.(formData);
                                  await handleGetAppointmentRooms(formData);
                                } finally {
                                  setIsRoomsLoading(false);
                                }
                              }}
                              disabled={
                                isSubmitting ||
                                isConsultationCompleted ||
                                !serviceType ||
                                !selectedDate ||
                                availableTimes.length === 0 ||
                                serviceUnavailableOnDay
                              }
                            />
                            <Label
                              htmlFor="show-all-times"
                              className={`text-xs cursor-pointer whitespace-nowrap ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              Show all service times
                            </Label>
                          </div>
                        </div>
                        {timeSelectLoading ? (
                          <Skeleton className="w-full h-10 animate-pulse" />
                        ) : (
                          <Select
                            value={selectedTime}
                            onValueChange={async (value) => {
                              setIsRoomsLoading(true);
                              clearBookedSlotNotice();
                              setValue("time", value);
                              const formData = {
                                ...getBookingFormData(),
                                skipAvailableSlots: true,
                              };
                              await onServiceTypeSelected?.(formData);
                              await handleGetAppointmentRooms(formData);
                              setIsRoomsLoading(false);
                            }}
                            disabled={
                              isSubmitting ||
                              isConsultationCompleted ||
                              !serviceType ||
                              !selectedDate ||
                              (showAllServiceTimes
                                ? availableTimes.length === 0
                                : bookableTimes.length === 0) ||
                              serviceUnavailableOnDay ||
                              pharmacyUnavailableOnDate
                            }
                          >
                            <SelectTrigger
                              className="w-full"
                              disabled={
                                isConsultationCompleted ||
                                !serviceType ||
                                !selectedDate ||
                                pharmacyUnavailableOnDate
                              }
                            >
                              <SelectValue placeholder="Select time" />
                            </SelectTrigger>
                            <SelectContentScrollable>
                              {showAllServiceTimes && adHocTimes.length > 0 ? (
                                <>
                                  <SelectGroup>
                                    <SelectLabel className="pl-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                      Available (bookable)
                                    </SelectLabel>
                                    {availableTimes.every((time) =>
                                      adHocTimes.some((a) => a.time === time),
                                    ) && (
                                      <div className="px-2 py-1.5 text-xs text-gray-500">
                                        No available slots
                                      </div>
                                    )}
                                    {availableTimes
                                      .filter(
                                        (time) =>
                                          !adHocTimes.some(
                                            (a) => a.time === time,
                                          ),
                                      )
                                      .map((time) => (
                                        <SelectItem key={time} value={time}>
                                          <span className="flex items-center gap-1.5">
                                            {time}
                                            {isSelectedSlotBookedForPharmacist &&
                                            time === selectedTime ? (
                                              <span
                                                className={`text-xs font-medium ${
                                                  isDarkMode
                                                    ? "text-red-400"
                                                    : "text-red-600"
                                                }`}
                                              >
                                                Already booked
                                              </span>
                                            ) : (
                                              <span className="text-xs font-medium text-green-600">
                                                Available
                                              </span>
                                            )}
                                          </span>
                                        </SelectItem>
                                      ))}
                                  </SelectGroup>
                                  <SelectSeparator />
                                  <SelectGroup>
                                    <SelectLabel className="pl-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                      Ad-hoc (outside normal availability)
                                    </SelectLabel>
                                    {adHocTimes.map(({ time, reasons }) => (
                                      <SelectItem key={time} value={time}>
                                        <span className="flex items-center gap-1.5">
                                          {time}
                                          <span
                                            className={`inline-flex items-center gap-1 text-xs font-medium ${
                                              isDarkMode
                                                ? "text-gray-400"
                                                : "text-gray-500"
                                            }`}
                                          >
                                            {reasons.join(", ")}
                                            <Info
                                              size={13}
                                              strokeWidth={2.3}
                                              className={
                                                isDarkMode
                                                  ? "text-amber-400"
                                                  : "text-amber-500"
                                              }
                                              title={`This time is outside normal availability: ${reasons.join(
                                                ", ",
                                              )}`}
                                            />
                                          </span>
                                        </span>
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                </>
                              ) : (
                                (showAllServiceTimes
                                  ? availableTimes
                                  : bookableTimes
                                ).map((time) => {
                                  const reasons = getSlotReasons(
                                    time,
                                    durationValueForSplit,
                                  );
                                  return (
                                    <SelectItem key={time} value={time}>
                                      <span className="flex items-center gap-1.5">
                                        {time}
                                        {reasons.length > 0 ? (
                                          <span
                                            className={`inline-flex items-center gap-1 text-xs font-medium ${
                                              isDarkMode
                                                ? "text-gray-400"
                                                : "text-gray-500"
                                            }`}
                                          >
                                            {reasons.join(", ")}
                                            <Info
                                              size={13}
                                              strokeWidth={2.3}
                                              className={
                                                isDarkMode
                                                  ? "text-amber-400"
                                                  : "text-amber-500"
                                              }
                                            />
                                          </span>
                                        ) : isSelectedSlotBookedForPharmacist &&
                                          time === selectedTime ? (
                                          <span
                                            className={`text-xs font-medium ${
                                              isDarkMode
                                                ? "text-red-400"
                                                : "text-red-600"
                                            }`}
                                          >
                                            Already booked
                                          </span>
                                        ) : (
                                          <span className="text-xs font-medium text-green-600">
                                            Available
                                          </span>
                                        )}
                                      </span>
                                    </SelectItem>
                                  );
                                })
                              )}
                            </SelectContentScrollable>
                          </Select>
                        )}
                        {bookedSlotMessage && (
                          <p
                            className={`text-sm transition-colors duration-300 ${
                              isDarkMode ? "text-red-400" : "text-red-600"
                            }`}
                          >
                            {bookedSlotMessage}
                          </p>
                        )}
                        {errors.time && (
                          <p
                            className={`text-sm transition-colors duration-300 ${
                              isDarkMode ? "text-red-400" : "text-red-600"
                            }
                           `}
                          >
                            {errors.time.message}
                          </p>
                        )}
                      </div>

                      {/* Pharmacist */}
                      <div className="space-y-2">
                        <Label
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-700"
                          }
                          `}
                        >
                          Pharmacist *
                        </Label>
                        {pharmacistSelectLoading ? (
                          <Skeleton className="w-full h-10 animate-pulse" />
                        ) : (
                          <Select
                            value={watch("pharmacist")}
                            onValueChange={async (value) => {
                              pharmacistPrefillPending.current = false;
                              setAvailabilityError(null);
                              setValue("pharmacist", value, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                              setIsRoomsLoading(true);
                              try {
                                const formData = {
                                  ...getBookingFormData(),
                                  pharmacist: value,
                                };
                                const slotsResponse =
                                  await onServiceTypeSelected?.(formData);
                                selectFirstAvailableSlot(slotsResponse);
                                await handleGetAppointmentRooms(formData);
                              } finally {
                                setIsRoomsLoading(false);
                              }
                            }}
                            disabled={
                              isSubmitting ||
                              isConsultationCompleted ||
                              !serviceType ||
                              !selectedDate ||
                              (showAllServiceTimes
                                ? availableTimes.length === 0
                                : bookableTimes.length === 0) ||
                              serviceUnavailableOnDay ||
                              pharmacyUnavailableOnDate
                            }
                          >
                            <SelectTrigger
                              className="w-full"
                              disabled={
                                isSubmitting ||
                                isEventCalendarLoading ||
                                isConsultationCompleted ||
                                !serviceType ||
                                !selectedDate ||
                                pharmacyUnavailableOnDate
                              }
                            >
                              <SelectValue placeholder="Select pharmacist" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem key="" value="">
                                Select pharmacist
                              </SelectItem>
                              {servicePharmacists.length > 1 && (
                                <SelectItem key={"any"} value={"any"}>
                                  Any Pharmacist
                                </SelectItem>
                              )}
                              {servicePharmacists.map((pharmacist) => (
                                <SelectItem
                                  key={pharmacist?.id}
                                  value={pharmacist?.id}
                                >
                                  {pharmacist?.name}
                                  {pharmacist?.isAlreadyBooked && (
                                    <span
                                      className={`ml-1.5 inline-flex translate-y-[1px] items-center ${
                                        isDarkMode
                                          ? "text-red-300"
                                          : "text-red-600"
                                      }`}
                                      title="Already booked"
                                    >
                                      <CircleAlert
                                        size={15}
                                        strokeWidth={2.5}
                                      />
                                    </span>
                                  )}
                                  {pharmacist?.isOutsideWorkingHours &&
                                    !pharmacist?.isAlreadyBooked && (
                                      <span
                                        className={`ml-1.5 inline-flex translate-y-[1px] items-center ${
                                          isDarkMode
                                            ? "text-amber-400"
                                            : "text-amber-500"
                                        }`}
                                        title="Outside working hours"
                                      >
                                        <TriangleAlert
                                          size={15}
                                          strokeWidth={2.3}
                                        />
                                      </span>
                                    )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {errors.pharmacist && (
                          <p
                            className={`text-sm transition-colors duration-300 ${
                              isDarkMode ? "text-red-400" : "text-red-600"
                            }
                            `}
                          >
                            {errors.pharmacist.message}
                          </p>
                        )}
                      </div>

                      {/* Duration */}
                      <div className="space-y-2">
                        <Label
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-700"
                          }`}
                        >
                          Duration *
                        </Label>
                        <Select
                          value={watch("duration")}
                          onValueChange={async (value) => {
                            clearBookedSlotNotice();
                            setValue("duration", value);
                            const formData = getBookingFormData();
                            const slotsResponse =
                              await onServiceTypeSelected?.(formData);
                            selectFirstAvailableSlot(slotsResponse);
                            await handleGetAppointmentRooms(formData);
                          }}
                          disabled={
                            isSubmitting ||
                            isEventCalendarLoading ||
                            isConsultationCompleted
                          }
                        >
                          <SelectTrigger
                            className="w-full"
                            disabled={
                              isSubmitting ||
                              isEventCalendarLoading ||
                              isConsultationCompleted
                            }
                          >
                            <SelectValue placeholder="Select duration" />
                          </SelectTrigger>
                          <SelectContent>
                            {durations.map((duration) => (
                              <SelectItem
                                key={duration.value}
                                value={duration.value}
                              >
                                {duration.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.duration && (
                          <p
                            className={`text-sm transition-colors duration-300 ${
                              isDarkMode ? "text-red-400" : "text-red-600"
                            }`}
                          >
                            {errors.duration.message}
                          </p>
                        )}
                      </div>

                      {/* Room */}
                      <div className="space-y-2">
                        <Label
                          className={`text-sm font-medium transition-colors duration-300 ${
                            isDarkMode ? "text-gray-200" : "text-gray-700"
                          }`}
                        >
                          {(() => {
                            const svc = (mappedServices || []).find(
                              (s: any) => s?._id === serviceType,
                            );
                            return svc && (svc as any).isRoomRequired
                              ? "Room *"
                              : "Room";
                          })()}
                        </Label>
                        {roomSelectLoading ? (
                          <Skeleton className="w-full h-10 animate-pulse" />
                        ) : (
                          <Select
                            value={watch("room") || "none"}
                            onValueChange={(value) =>
                              setValue("room", value === "none" ? "" : value)
                            }
                            disabled={
                              isSubmitting ||
                              isEventCalendarLoading ||
                              isConsultationCompleted
                            }
                          >
                            <SelectTrigger
                              className="w-full"
                              disabled={
                                isSubmitting ||
                                isEventCalendarLoading ||
                                isConsultationCompleted
                              }
                            >
                              <SelectValue
                                placeholder={(() => {
                                  const svc = (mappedServices || []).find(
                                    (s: any) => s?._id === serviceType,
                                  );
                                  return svc && (svc as any).isRoomRequired
                                    ? "Select room"
                                    : "Select room (optional)";
                                })()}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                No room assigned
                              </SelectItem>
                              {allAvailableRooms.map((room: any) => (
                                <SelectItem key={room.id} value={room.id}>
                                  {room.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {errors.room && (
                          <p
                            className={`text-sm transition-colors duration-300 ${
                              isDarkMode ? "text-red-400" : "text-red-600"
                            }`}
                          >
                            {errors.room.message}
                          </p>
                        )}
                      </div>

                      {pharmacyUnavailableOnDate && (
                        <Alert
                          variant="destructive"
                          className={
                            isDarkMode
                              ? "col-span-2 bg-red-900/20 text-red-300 border-red-800"
                              : "col-span-2"
                          }
                        >
                          <AlertTriangle className="w-4 h-4" />
                          <AlertDescription>
                            The pharmacy is not available on this date. Please
                            choose a different date.
                          </AlertDescription>
                        </Alert>
                      )}

                      {pharmacistAlreadyBooked && (
                        <Alert
                          variant="destructive"
                          className={
                            isDarkMode
                              ? "col-span-2 bg-red-900/20 text-red-300 border-red-800"
                              : "col-span-2"
                          }
                        >
                          <AlertTriangle className="w-4 h-4" />
                          <AlertDescription>
                            This pharmacist already has an appointment booked
                            for the selected time and cannot be booked. Please
                            choose a different pharmacist or appointment time.
                          </AlertDescription>
                        </Alert>
                      )}

                      {pharmacistUnavailable &&
                        !pharmacistAlreadyBooked &&
                        !isEditMode &&
                        !!selectedTime && (
                          <Alert
                            className={`col-span-2 border-amber-300 ${
                              isDarkMode
                                ? "bg-amber-900/20 text-amber-200"
                                : "bg-amber-50 text-amber-800"
                            }`}
                          >
                            <AlertTriangle className="w-4 h-4" />
                            <AlertDescription>
                              {selectedPharmacistName} is not scheduled to work
                              at this date and time based on their working
                              hours. You can still create an ad-hoc appointment
                              by selecting Create Appointment.
                            </AlertDescription>
                          </Alert>
                        )}

                      {noPharmacistAvailable && (
                        <Alert
                          variant="destructive"
                          className={
                            isDarkMode
                              ? "col-span-2 bg-red-900/20 text-red-300 border-red-800"
                              : "col-span-2"
                          }
                        >
                          <AlertTriangle className="w-4 h-4" />
                          <AlertDescription>
                            No pharmacist is available at this date and time —
                            every pharmacist is either outside their working
                            hours or already booked. Please choose a different
                            date, time, or pharmacist before creating this
                            appointment.
                          </AlertDescription>
                        </Alert>
                      )}

                      {!pharmacistSelectLoading &&
                        !timeSelectLoading &&
                        !isPharmacyServiceLoading &&
                        serviceUnavailableOnDay && (
                          <Alert
                            className={`col-span-2 border-amber-300 ${
                              isDarkMode
                                ? "bg-amber-900/20 text-amber-200"
                                : "bg-amber-50 text-amber-800"
                            }`}
                          >
                            <AlertTriangle className="w-4 h-4" />
                            <AlertDescription>
                              {selectedServiceInfo?.serviceStartDate
                                ? `This service will be available on ${new Date(
                                    selectedServiceInfo.serviceStartDate,
                                  ).toLocaleDateString("en-US", {
                                    month: "2-digit",
                                    day: "2-digit",
                                    year: "numeric",
                                  })}`
                                : "This service is not available"}
                            </AlertDescription>
                          </Alert>
                        )}
                    </div>
                  </AccordionContent>
                )}
              </AccordionItem>

              <AccordionItem
                value="notifications"
                className={`border-b transition-colors duration-300 ${
                  isDarkMode ? "border-gray-600" : "border-gray-200"
                }`}
              >
                <AccordionTrigger
                  className={`px-0 py-4 ${
                    isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2 font-semibold">
                    <Bell className="w-5 h-5" />
                    <span>Notifications</span>
                    {watch("confirmationMethod") && (
                      <span
                        className={`text-xs font-normal ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        (
                        {watch("confirmationMethod") === "email"
                          ? "Email"
                          : watch("confirmationMethod") === "mobile"
                            ? "SMS"
                            : "Do not send"}
                        )
                      </span>
                    )}
                    {!watch("confirmationMethod") && (
                      <span
                        className={`text-xs font-normal ${
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }`}
                      >
                        (Do not send)
                      </span>
                    )}
                  </div>
                </AccordionTrigger>

                <AccordionContent className="pt-0 px-2 pb-6 !overflow-visible">
                  {/* Contact Information */}
                  <div className="col-span-2 space-y-2">
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Contact Information
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label
                          className={`text-xs ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          Email
                        </Label>
                        <Input
                          type="email"
                          inputMode="email"
                          autoComplete="email"
                          maxLength={254}
                          placeholder="name@email.com"
                          pattern="^[^\s@]+@[^\s@]+\.[^\s@]+$"
                          disabled={isConsultationCompleted}
                          {...register("email", {
                            onChange: (e) => {
                              const val = (
                                e.target as HTMLInputElement
                              ).value.replace(/\s+/g, "");
                              setValue("email", val, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            },
                            onBlur: (e) => {
                              setValue(
                                "email",
                                (e.target as HTMLInputElement).value.trim(),
                                { shouldValidate: true },
                              );
                            },
                          })}
                          className={`${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-200"
                          } ${
                            errors.email
                              ? "border-red-500 focus-visible:ring-red-500"
                              : ""
                          }`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label
                          className={`text-xs ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          Mobile
                        </Label>
                        <Input
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          maxLength={20}
                          placeholder="07700 900123"
                          disabled={isConsultationCompleted}
                          {...register("mobile", {
                            onChange: (e) => {
                              const raw = (e.target as HTMLInputElement).value;
                              // Allow digits, spaces, hyphens, and leading +
                              let sanitized = raw.replace(/[^0-9+\s\-()]/g, "");
                              if (sanitized.includes("+")) {
                                sanitized =
                                  (sanitized.startsWith("+") ? "+" : "") +
                                  sanitized.replace(/\+/g, "");
                              }
                              // Prevent multiple consecutive hyphens or spaces
                              sanitized = sanitized
                                .replace(/-{2,}/g, "-")
                                .replace(/\s{2,}/g, " ");
                              setValue("mobile", sanitized, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            },
                            onBlur: (e) => {
                              setValue(
                                "mobile",
                                (e.target as HTMLInputElement).value.trim(),
                                { shouldValidate: true },
                              );
                            },
                          })}
                          className={`${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-200"
                          } ${
                            errors.mobile
                              ? "border-red-500 focus-visible:ring-red-500"
                              : ""
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Confirmation method */}
                  <div className="mb-4 mt-4 space-y-3">
                    <Label
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Send booking confirmation via
                    </Label>
                    <RadioGroup
                      value={(watch("confirmationMethod") as any) || "none"}
                      disabled={BookingConfirmationLoading}
                      onValueChange={(v) =>
                        setValue(
                          "confirmationMethod",
                          (v === "none" ? undefined : v) as any,
                          {
                            shouldValidate: true,
                          },
                        )
                      }
                      className="grid grid-cols-1 gap-4"
                    >
                      <div
                        className={`flex items-center gap-2 p-3 rounded-md border ${
                          isDarkMode ? "border-gray-700" : "border-gray-200"
                        }`}
                      >
                        <RadioGroupItem
                          value="email"
                          id="confirm-email"
                          disabled={
                            !watch("email") ||
                            Boolean((errors as any).email) ||
                            isConsultationCompleted
                          }
                        />
                        <Label htmlFor="confirm-email" className="flex-1">
                          Email
                          <span
                            className={`block text-xs mt-0.5 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {watch("email") || "No email provided"}
                          </span>
                        </Label>
                      </div>
                      <div
                        className={`flex items-center gap-2 p-3 rounded-md border ${
                          isDarkMode ? "border-gray-700" : "border-gray-200"
                        }`}
                      >
                        <RadioGroupItem
                          value="mobile"
                          id="confirm-mobile"
                          disabled={
                            !watch("mobile") ||
                            !(watch("mobile") || "").replace(/\D/g, "") ||
                            Boolean((errors as any).mobile) ||
                            isConsultationCompleted
                          }
                        />
                        <Label htmlFor="confirm-mobile" className="flex-1">
                          Mobile (SMS)
                          <span
                            className={`block text-xs mt-0.5 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {watch("mobile") &&
                            (watch("mobile") || "").replace(/\D/g, "")
                              ? watch("mobile")
                              : "No mobile provided"}
                          </span>
                        </Label>
                      </div>
                      <div
                        className={`flex items-center gap-2 p-3 rounded-md border ${
                          isDarkMode ? "border-gray-700" : "border-gray-200"
                        }`}
                      >
                        <RadioGroupItem
                          value="none"
                          id="confirm-none"
                          disabled={isConsultationCompleted}
                        />
                        <Label htmlFor="confirm-none" className="flex-1">
                          None (Default)
                          <span
                            className={`block text-xs mt-0.5 ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            Do not send
                          </span>
                        </Label>
                      </div>
                    </RadioGroup>
                    {errors.confirmationMethod && (
                      <p className="mt-1 text-sm text-red-500">
                        {errors.confirmationMethod.message}
                      </p>
                    )}
                  </div>

                  {/* Event Name */}
                  <div
                    className={`space-y-2 pt-4 border-t ${
                      isDarkMode ? "text-gray-200" : "text-gray-700"
                    }`}
                  >
                    <Label
                      htmlFor="notes"
                      className={`text-sm font-medium transition-colors duration-300 ${
                        isDarkMode ? "text-gray-200" : "text-gray-700"
                      }`}
                    >
                      Notes (optional)
                    </Label>
                    <Input
                      id="notes"
                      type="text"
                      placeholder="Additional notes or instructions"
                      disabled={
                        isEventCalendarLoading || isConsultationCompleted
                      }
                      {...register("notes")}
                      maxLength={NOTES_MAX_LENGTH}
                      className="w-full"
                    />
                    {(errors.notes ||
                      (watch("notes") || "").length >= NOTES_MAX_LENGTH) && (
                      <p
                        className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? "text-red-400" : "text-red-600"
                        }`}
                      >
                        {charLimitMessage(NOTES_MAX_LENGTH)}
                      </p>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* TASKS TO COMPLETE SECTION */}
              {(localTasks?.length > 0 || !isReadOnly) && (
                <AccordionItem
                  value="tasks"
                  className={`border-b transition-colors duration-300 ${
                    isDarkMode ? "border-gray-600" : "border-gray-200"
                  }`}
                >
                  <AccordionTrigger
                    className={`px-0 py-4 ${
                      isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 font-semibold">
                      <CheckSquare className="w-5 h-5" />
                      <span>Tasks to Complete</span>
                      {localTasks?.length > 0 && (
                        <span className="inline-flex items-center justify-center w-6 h-6 ml-2 text-xs font-bold text-white bg-blue-600 rounded-full">
                          {localTasks.length}
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-0 pb-6 !overflow-visible">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        {localTasks?.map((task) => (
                          <div
                            key={task.id}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              isDarkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <span
                              className={`text-sm ${
                                isDarkMode ? "text-gray-200" : "text-gray-900"
                              }`}
                            >
                              {task.label}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveTask(task.id)}
                              className={`p-1 rounded hover:${
                                isDarkMode ? "bg-gray-600" : "bg-gray-200"
                              }`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Input
                          type="text"
                          placeholder="Add new task"
                          value={taskLabel}
                          onChange={(e) =>
                            handleTaskLabelChange(e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && taskLabel.trim()) {
                              e.preventDefault();
                              handleAddTask(taskLabel);
                            }
                          }}
                          disabled={isReadOnly}
                          maxLength={TASK_MAX_LENGTH}
                          className={`${
                            isDarkMode
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-200"
                          }`}
                        />
                        <Button
                          type="button"
                          onClick={() => handleAddTask(taskLabel)}
                          disabled={isReadOnly || !taskLabel.trim()}
                          size="sm"
                          className="gap-1"
                        >
                          <Plus className="w-4 h-4" />
                          Add
                        </Button>
                      </div>
                      {taskLabel.length >= TASK_MAX_LENGTH && (
                        <p
                          className={`text-sm transition-colors duration-300 ${
                            isDarkMode ? "text-red-400" : "text-red-600"
                          }`}
                        >
                          {charLimitMessage(TASK_MAX_LENGTH)}
                        </p>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              {/* VACCINES TO ADMINISTER SECTION */}
              {(localVaccines.length > 0 || !isReadOnly) && (
                <AccordionItem
                  value="vaccines"
                  className={`border-b transition-colors duration-300 ${
                    isDarkMode ? "border-gray-600" : "border-gray-200"
                  }`}
                >
                  <AccordionTrigger
                    className={`px-0 py-4 ${
                      isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 font-semibold">
                      <Syringe className="w-5 h-5" />
                      <span>Vaccines to Administer</span>
                      {localVaccines.length > 0 && (
                        <span className="inline-flex items-center justify-center w-6 h-6 ml-2 text-xs font-bold text-white bg-purple-600 rounded-full">
                          {localVaccines.length}
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-0 pb-6 !overflow-visible">
                    <div className="space-y-4">
                      <div className="space-y-2 overflow-y-auto max-h-48">
                        {localVaccines.map((vaccine) => (
                          <div
                            key={vaccine.id}
                            className={`flex items-start justify-between p-3 rounded-lg border ${
                              isDarkMode
                                ? "bg-gray-700 border-gray-600"
                                : "bg-gray-50 border-gray-200"
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <p
                                className={`text-sm font-medium ${
                                  isDarkMode ? "text-gray-200" : "text-gray-900"
                                }`}
                              >
                                {vaccine.name}
                              </p>
                              {vaccine.dose && (
                                <p
                                  className={`text-xs mt-1 ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {vaccine.dose}
                                </p>
                              )}
                              {vaccine.notes && (
                                <p
                                  className={`text-xs mt-1 ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {vaccine.notes}
                                </p>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveVaccine(vaccine.id)}
                              className={`p-1 rounded hover:${
                                isDarkMode ? "bg_gray-600" : "bg-gray-200"
                              } ml-2 flex-shrink-0`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="pt-2 space-y-2">
                        <VaccineCombobox
                          value={vaccineIdState}
                          onChange={handleVaccineIdChange}
                          placeholder="Search vaccine..."
                          isDarkMode={isDarkMode}
                          disabled={isReadOnly}
                          onEnterKey={() => {
                            if (vaccineIdState.trim()) {
                              handleAddVaccine(vaccineIdState);
                            }
                          }}
                          onSelect={(id) => {
                            handleVaccineIdChange(id);
                            handleAddVaccine(id);
                          }}
                        />
                        {/* <Button
                        type="button"
                        onClick={() => {
                          if (vaccineIdState.trim()) {
                            handleAddVaccine(vaccineIdState);
                          }
                        }}
                        disabled={isReadOnly || !vaccineIdState.trim()}
                        size="sm"
                        className="w-full gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Vaccine
                      </Button> */}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}
            </Accordion>

            {/* Inline form error (e.g., duplicate/validation) */}
            {submitError && (
              <div
                className={`mt-2 p-3 rounded border ${
                  isDarkMode
                    ? "bg-red-900/30 border-red-700 text-red-200"
                    : "bg-red-50 border-red-200 text-red-700"
                }`}
              >
                {submitError}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="flex-1"
                disabled={isEventCalendarLoading}
              >
                Cancel
              </Button>

              {isEditMode && onDelete && currentBooking && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => handleDelete()}
                  className="flex-1"
                  disabled={
                    isEventCalendarLoading ||
                    (currentBooking as any).eventStatus === 1
                  }
                >
                  Delete
                </Button>
              )}

              {hasPatientId && onStartConsultation && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (!hasConsultationPermission) {
                      setShowPermissionMessage(
                        "You don't have permission to start this consultation",
                      );
                      setShowPermissionModal(true);
                      return;
                    }

                    onStartConsultation(booking);
                  }}
                  className="flex-1 text-white bg-green-600 border-green-600 hover:bg-green-700"
                  disabled={isStartConsultationDisabled}
                >
                  Start Consultation
                </Button>
              )}

              <Button
                type="submit"
                className="flex-1 text-white bg-slate-900 hover:bg-slate-800"
                disabled={
                  isSubmitting ||
                  isEventCalendarLoading ||
                  (isEditMode && (currentBooking as any)?.eventStatus === 1) ||
                  (isEditMode && (currentBooking as any)?.eventStatus === 4) ||
                  isConsultationCompleted ||
                  pharmacistAlreadyBooked ||
                  serviceUnavailableOnDay ||
                  noPharmacistAvailable
                }
              >
                {isSubmitting ? (
                  <span className="inline-flex items-center gap-1">
                    {isEditMode ? "Updating" : "Creating"}
                    <span className="">...</span>
                  </span>
                ) : isEditMode ? (
                  "Update Appointment"
                ) : (
                  "Create Appointment"
                )}
              </Button>
            </div>
          </form>
          {/* Delete Confirmation Dialog */}
          {showDeleteConfirm && isEditMode && onDelete && currentBooking && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div
                className={`p-6 rounded-lg max-w-md w-full mx-4 ${
                  isDarkMode
                    ? "bg-gray-800 text-white"
                    : "bg-white text-gray-900"
                }`}
              >
                <h3 className="mb-4 text-lg font-semibold">
                  Delete Appointment
                </h3>
                <p
                  className={`${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  } mb-6`}
                >
                  Are you sure you want to delete this appointment? This action
                  cannot be undone.
                </p>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1"
                    disabled={isEventCalendarLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      onDelete(currentBooking.id);
                      setShowDeleteConfirm(false);
                      setOpen(false);
                    }}
                    className="flex-1"
                    disabled={isEventCalendarLoading}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PatientMatchDialog
        open={showMatchDialog}
        onOpenChange={setShowMatchDialog}
        onMatch={handlePatientMatch}
        isDarkMode={isDarkMode}
        // Use the NHS patient data
        matchPatient={{
          name:
            NHSEmergencyPatientData?.[0]?.patientName ||
            watch("patientName") ||
            "New Patient",
          consultationId: NHSEmergencyPatientData?.[0]?.consultationId || "",
        }}
        NHSEmergencyPatientData={(() => {
          // The newly added patient (first in the list)
          const sourcePatient = localMatchingPatients[0];
          // Other patients to compare against (excluding the source)
          const patientsToCompare =
            localMatchingPatients.length > 1
              ? localMatchingPatients.slice(1)
              : [];

          return patientsToCompare.map((patient, index) => ({
            _id: patient.patientID || patient._id || `temp-${index}`,
            patientID: patient.patientID || patient._id,
            patientName:
              patient.patientName ||
              `${patient.foreName || ""} ${patient.surName || ""}`.trim(),
            nhsNumber: patient.nhsNumber || "Not available",
            dob: patient.dob || new Date().toISOString(),
            address: patient.address || "Address not available",
            // Compare against the source patient
            matchScore: calculateMatchScore(patient, sourcePatient),
            matchReasons: generateMatchReasons(patient, sourcePatient),
            consultationId: patient.consultationId || "",
          }));
        })()}
        isNHSEmergencyPatientLoading={isNHSEmergencyPatientLoading}
        handleAddNewPatient={handleAddNewPatient}
      />
    </>
  );
}
